import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useGymStore, EXERCISES, MuscleGroup, Exercise, GYM_PRODUCTS, GYM_ROUTINES, Routine, GYM_TRAINERS } from '../state/useGymStore';
import { muscleWikiData } from '../data/muscleWikiData';
import { useExperienceStore } from '../state/useExperienceStore';
import { useAuth } from '../lib/AuthContext';
import { 
  Dumbbell, 
  ChevronLeft, 
  Info, 
  Play, 
  Target, 
  Zap,
  X,
  ArrowRight,
  ShoppingBag,
  CreditCard,
  Gift,
  Star,
  Clock,
  Layout,
  Users,
  Search,
  User,
  Home,
  Star as StarIcon,
  BrainCircuit,
  Microscope,
  Sparkles,
  ShieldAlert,
  Plus,
  Trash2,
  Edit3,
  Eye,
  Store
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const MUSCLE_NAMES: Record<MuscleGroup, string> = {
  chest: 'Pecho',
  back: 'Espalda',
  shoulders: 'Hombros',
  quads: 'Cuádriceps',
  hamstrings: 'Isquios',
  calves: 'Gemelos',
  biceps: 'Biceps',
  triceps: 'Triceps',
  core: 'Core',
  glutes: 'Glúteos',
  forearms: 'Antebrazos',
  lower_back: 'Lumbares'
};

const MuscleIcon = ({ name, className }: { name: string, className?: string }) => {
  switch (name) {
    case 'Target': return <Target className={className} />;
    case 'Layout': return <Layout className={className} />;
    case 'Zap': return <Zap className={className} />;
    case 'Sparkles': return <Sparkles className={className} />;
    case 'Dumbbell':
    default:
      return <Dumbbell className={className} />;
  }
};

const MUSCLE_CARDS = [
  { id: 'chest', name: 'Pecho / Pectorales', category: 'Superior', description: 'Pectorales mayores y menores. Enfoque en empuje y volumen muscular.', icon: 'Target', view: 'front' },
  { id: 'back', name: 'Espalda / Dorsales', category: 'Superior', description: 'Dorsales, trapecios y lumbares. Esencial para la tracción.', icon: 'Layout', view: 'back' },
  { id: 'shoulders', name: 'Hombros / Deltoides', category: 'Superior', description: 'Deltoides anterior, lateral y posterior para hombros fuertes.', icon: 'Zap', view: 'front' },
  { id: 'biceps', name: 'Bíceps', category: 'Superior', description: 'Flexores de brazo para fuerza y contracción de pico.', icon: 'Dumbbell', view: 'front' },
  { id: 'triceps', name: 'Tríceps', category: 'Superior', description: 'Extensores de codo. Aportan el 60% del volumen del brazo.', icon: 'Dumbbell', view: 'back' },
  { id: 'forearms', name: 'Antebrazos', category: 'Superior', description: 'Fuerza de agarre, flexores y extensores de muñeca.', icon: 'Dumbbell', view: 'front' },
  { id: 'core', name: 'Core / Abdominales', category: 'Core', description: 'Estabilidad central, abdominales y oblicuos.', icon: 'Target', view: 'front' },
  { id: 'lower_back', name: 'Lumbares / Espalda Baja', category: 'Core', description: 'Soporte lumbar y cadena posterior.', icon: 'Layout', view: 'back' },
  { id: 'glutes', name: 'Glúteos', category: 'Inferior', description: 'Glúteo mayor y medio. Estabilidad de cadera y potencia.', icon: 'Sparkles', view: 'back' },
  { id: 'quads', name: 'Cuádriceps', category: 'Inferior', description: 'Frente del muslo. Potencia y estabilidad de rodilla.', icon: 'Sparkles', view: 'front' },
  { id: 'hamstrings', name: 'Isquios / Femorales', category: 'Inferior', description: 'Posterior del muslo. Fuerza de flexión.', icon: 'Sparkles', view: 'back' },
  { id: 'calves', name: 'Gemelos / Pantorrillas', category: 'Inferior', description: 'Flexión plantar. Soporte de tobillo y potencia.', icon: 'Sparkles', view: 'back' }
] as const;

// Professional Anatomical Map Component (MuscleWiki Style Upgrade)
const BodyMap = ({ gender, view, onSelect, selectedMuscle }: { gender: 'male' | 'female', view: 'front' | 'back', onSelect: (m: MuscleGroup) => void, selectedMuscle: MuscleGroup | null }) => {
  const frontMuscles: { id: MuscleGroup, label: string, top: string, left: string, area: string }[] = [
    { id: 'shoulders', label: 'Hombros', top: '15%', left: '22%', area: 'w-16 h-8' },
    { id: 'shoulders', label: 'Hombros', top: '15%', left: '78%', area: 'w-16 h-8' },
    { id: 'chest', label: 'Pecho', top: '24%', left: '50%', area: 'w-20 h-10' },
    { id: 'biceps', label: 'Bícep', top: '34%', left: '18%', area: 'w-16 h-8' },
    { id: 'biceps', label: 'Bícep', top: '34%', left: '82%', area: 'w-16 h-8' },
    { id: 'forearms', label: 'Antebrazo', top: '48%', left: '12%', area: 'w-16 h-8' },
    { id: 'forearms', label: 'Antebrazo', top: '48%', left: '88%', area: 'w-16 h-8' },
    { id: 'core', label: 'Abs', top: '44%', left: '50%', area: 'w-16 h-10' },
    { id: 'quads', label: 'Cuádriceps', top: '68%', left: '32%', area: 'w-20 h-10' },
    { id: 'quads', label: 'Cuádriceps', top: '68%', left: '68%', area: 'w-20 h-10' },
  ];

  const backMuscles: { id: MuscleGroup, label: string, top: string, left: string, area: string }[] = [
    { id: 'back', label: 'Espalda', top: '28%', left: '50%', area: 'w-20 h-10' },
    { id: 'triceps', label: 'Tríceps', top: '32%', left: '18%', area: 'w-16 h-8' },
    { id: 'triceps', label: 'Tríceps', top: '32%', left: '82%', area: 'w-16 h-8' },
    { id: 'lower_back', label: 'Lumbar', top: '46%', left: '50%', area: 'w-18 h-8' },
    { id: 'glutes', label: 'Glúteo', top: '56%', left: '50%', area: 'w-20 h-10' },
    { id: 'hamstrings', label: 'Isquios', top: '72%', left: '32%', area: 'w-20 h-10' },
    { id: 'hamstrings', label: 'Isquios', top: '72%', left: '68%', area: 'w-20 h-10' },
    { id: 'calves', label: 'Gemelos', top: '88%', left: '32%', area: 'w-18 h-8' },
    { id: 'calves', label: 'Gemelos', top: '88%', left: '68%', area: 'w-18 h-8' },
  ];

  const muscles = view === 'front' ? frontMuscles : backMuscles;

  return (
    <div className="relative w-full aspect-[4/8] max-w-[340px] md:max-w-[360px] mx-auto group">
      {/* Background Silhouette Glow */}
      <div className="absolute inset-0 bg-cyan-500/5 blur-[120px] rounded-full pointer-events-none" />
      
      {/* Anatomical Base Silhouette (Futuristic Hologram) */}
      <div className="absolute inset-0 flex items-center justify-center opacity-40 pointer-events-none">
        <div className="w-[88%] h-[96%] bg-cyan-950/10 rounded-[35%_35%_25%_25%_/_50%_50%_40%_40%] border border-cyan-500/10 backdrop-blur-sm relative overflow-hidden">
          {/* Cyber scan lines */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(18,18,24,0)_95%,rgba(6,182,212,0.15)_95%)] bg-[length:100%_12px] animate-[scan_8s_linear_infinite]" />
        </div>
      </div>

      {/* Futuristic Human Vector Wireframe */}
      <svg className="absolute inset-0 w-full h-full opacity-35 pointer-events-none" viewBox="0 0 100 200" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M50 10 V190 M10 100 H90 M20 50 L80 150 M80 50 L20 150" stroke="rgba(6, 182, 212, 0.08)" strokeWidth="0.5" strokeDasharray="1 3" />
        {view === 'front' ? (
          <g stroke="rgba(6, 182, 212, 0.45)" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="50" cy="22" r="9" />
            <path d="M48 31 V35 H52 V31" />
            <path d="M36 38 H64 L60 82 H40 L36 38 Z" />
            <path d="M36 48 H64 M50 38 V82" />
            <path d="M36 38 L26 62 L21 85" />
            <path d="M64 38 L74 62 L79 85" />
            <path d="M40 82 L43 108 H57 L60 82 Z" />
            <path d="M43 108 L39 146 L36 182" />
            <path d="M57 108 L61 146 L64 182" />
          </g>
        ) : (
          <g stroke="rgba(168, 85, 247, 0.45)" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="50" cy="22" r="9" />
            <path d="M50 31 V108" />
            <path d="M35 38 H65 L61 82 H39 L35 38 Z" />
            <path d="M41 31 L50 40 L59 31" />
            <path d="M35 38 L25 62 L20 85" />
            <path d="M65 38 L75 62 L80 85" />
            <path d="M39 82 L42 108 H58 L61 82 Z" />
            <path d="M42 108 L38 146 L35 182" />
            <path d="M58 108 L62 146 L65 182" />
          </g>
        )}
      </svg>

      <AnimatePresence mode="wait">
        <motion.div
          key={`${view}-${gender}`}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="absolute inset-0"
        >
          {muscles.map((m, i) => (
            <motion.button
              key={`${m.id}-${i}`}
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onSelect(m.id)}
              className={`absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center transition-all duration-300 ${m.area} group/muscle z-20`}
              style={{ top: m.top, left: m.left }}
            >
              {/* Muscle Zone Highlight (Glow & Glassmorphic Pill) */}
              <div className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider backdrop-blur-md transition-all duration-300 flex items-center gap-1.5 border ${
                selectedMuscle === m.id 
                ? 'bg-cyan-500/20 border-cyan-400 text-cyan-200 shadow-[0_0_20px_rgba(6,182,212,0.4)] ring-2 ring-cyan-500/20' 
                : 'bg-black/75 border-white/10 text-white/70 hover:border-cyan-400/50 hover:bg-black/90 hover:text-white hover:shadow-[0_0_15px_rgba(6,182,212,0.2)]'
              }`}>
                <div className={`w-1.5 h-1.5 rounded-full ${
                  selectedMuscle === m.id 
                  ? 'bg-cyan-400 animate-ping' 
                  : 'bg-white/30 group-hover/muscle:bg-cyan-400'
                }`} />
                {m.label}
              </div>
            </motion.button>
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export const GymOverlay = () => {
  const { mode, setMode } = useExperienceStore();
  const { 
    gender,
    setGender,
    bodyView,
    setBodyView,
    selectedMuscle, 
    setSelectedMuscle, 
    selectedExercise, 
    setSelectedExercise,
    selectedRoutine,
    setSelectedRoutine,
    hasLoyaltyCard,
    loyaltyPoints,
    toggleLoyaltyCard,
    addPoints
  } = useGymStore();
  const { selectedTrainer, setSelectedTrainer } = useGymStore();

  const [activeTab, setActiveTab] = useState<'TRAINING' | 'WIKI' | 'ROUTINES' | 'HOME' | 'COACHES' | 'STORE' | 'COMMUNITY' | 'CALCS' | 'MASTER'>('TRAINING');
  const [selectedWikiMuscle, setSelectedWikiMuscle] = useState<string>('pectorals');
  const [showcaseTrainerId, setShowcaseTrainerId] = useState<string | null>(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState<{productName: string, price: number, pts: number} | null>(null);
  const [storeCategory, setStoreCategory] = useState<'Todos' | 'Ropa' | 'Nutrición' | 'Comunidad'>('Todos');
  const [muscleCategoryFilter, setMuscleCategoryFilter] = useState<'Todos' | 'Superior' | 'Inferior' | 'Core'>('Todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState<string | 'Todos'>('Todos');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string | 'Todos'>('Todos');
  const [isAiCoaching, setIsAiCoaching] = useState(false);
  const [aiTip, setAiTip] = useState('');
  const [showAiTip, setShowAiTip] = useState(false);

  // Administration State Engine
  const [trainersList, setTrainersList] = useState<any[]>(GYM_TRAINERS);
  const [productsList, setProductsList] = useState<any[]>(GYM_PRODUCTS);
  const [routinesList, setRoutinesList] = useState<any[]>(GYM_ROUTINES);
  const [adminActiveSubTab, setAdminActiveSubTab] = useState<'coaches' | 'products' | 'routines'>('coaches');

  const [editingTrainer, setEditingTrainer] = useState<any | null>(null);
  const [editingProduct, setEditingProduct] = useState<any | null>(null);
  const [editingRoutine, setEditingRoutine] = useState<any | null>(null);

  const [trainerForm, setTrainerForm] = useState({ id: '', name: '', specialty: '', experience: '', bio: '', image: '' });
  const [productForm, setProductForm] = useState({ id: '', name: '', category: 'Ropa' as any, price: 0, description: '', image: '', trainerId: '' });
  const [routineForm, setRoutineForm] = useState({ id: '', name: '', description: '', difficulty: 'Principiante' as any, duration: '30 min', image: '', trainerId: '' });

  // Calculator states
  const [weight, setWeight] = useState(70);
  const [height, setHeight] = useState(175);
  const [age, setAge] = useState(25);
  const [goal, setGoal] = useState<'cut' | 'bulk' | 'maintain'>('maintain');

  const fetchAiCoachTip = async (exercise: Exercise) => {
    if (aiTip && showAiTip) return; // Already fetched and showing
    
    setIsAiCoaching(true);
    setShowAiTip(true);
    setAiTip('Analizando biomecánica y biofísica con IA...');
    
    try {
      const response = await fetch("/api/ai/coach", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exerciseName: exercise.name }),
      });

      if (!response.ok) {
        throw new Error("Proxy error or missing key");
      }

      const data = await response.json();
      setAiTip(data.text || 'LA CONTRACCIÓN DEBE SER CONTROLADA EN LA FASE EXCÉNTRICA PARA MAXIMIZAR EL RECLUTAMIENTO DE FIBRAS.');
    } catch (error) {
      console.error("AI Coach Proxy Error:", error);
      setAiTip('LA CONTRACCIÓN DEBE SER CONTROLADA EN LA FASE EXCÉNTRICA PARA MAXIMIZAR EL RECLUTAMIENTO DE FIBRAS.');
    } finally {
      setIsAiCoaching(false);
    }
  };

  useEffect(() => {
    // Reset AI tip when changing exercise
    setAiTip('');
    setShowAiTip(false);
  }, [selectedExercise]);

  if (mode !== 'GYM') return null;

  const filteredExercises = EXERCISES.filter(e => {
    const matchesMuscle = !selectedMuscle || e.muscle === selectedMuscle;
    const matchesSearch = e.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesEquipment = selectedEquipment === 'Todos' || e.equipment === selectedEquipment;
    const matchesDifficulty = selectedDifficulty === 'Todos' || e.difficulty === selectedDifficulty;
    return matchesMuscle && matchesSearch && matchesEquipment && matchesDifficulty;
  });
  
  const getRoutineExercises = (routine: Routine) => {
    return EXERCISES.filter(e => routine.exerciseIds.includes(e.id));
  };

  const filteredProducts = storeCategory === 'Todos' 
    ? productsList 
    : productsList.filter(p => p.category === storeCategory);

    const { profile } = useAuth();

  return (
    <div className="absolute inset-0 z-20 pointer-events-none flex flex-col p-2 md:p-8 lg:p-12 gap-2 md:gap-6 overflow-hidden text-white">
      {/* Header */}
      <motion.div 
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full flex items-center justify-between bg-black/40 backdrop-blur-xl border border-white/5 rounded-2xl p-4 md:p-6 pointer-events-auto shadow-xl"
      >
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-3 text-cyan-400 cursor-pointer" onClick={() => { setActiveTab('TRAINING'); setSelectedMuscle(null); setSearchQuery(''); }}>
            <Dumbbell className="w-6 h-6" />
            <h2 className="text-xl font-black uppercase tracking-tighter italic">Empr<span className="text-white">Wiki</span></h2>
            {profile?.role === 'admin' && (
              <div className="ml-4 flex items-center gap-2 px-3 py-1 bg-red-500/20 border border-red-500/40 rounded-full">
                <ShieldAlert className="w-3 h-3 text-red-400" />
                <span className="text-[8px] font-black text-red-400 uppercase tracking-widest">Super Admin Access</span>
              </div>
            )}
          </div>
          <nav className="hidden lg:flex items-center gap-10">
            <div className="flex flex-col">
              <span className="text-[7px] font-black text-cyan-400 uppercase tracking-[0.5em] mb-1">Perfil Atlético</span>
              <button 
                onClick={() => setActiveTab('TRAINING')}
                className={`text-[10px] font-black uppercase tracking-[0.3em] transition-all ${activeTab === 'TRAINING' ? 'text-cyan-400' : 'text-white/20 hover:text-white'}`}
              >
                ESPADA & CORE
              </button>
            </div>
            <button 
              onClick={() => setActiveTab('WIKI')}
              className={`text-[10px] font-black uppercase tracking-[0.3em] transition-all ${activeTab === 'WIKI' ? 'text-cyan-400' : 'text-white/20 hover:text-white'}`}
            >
              WIKI
            </button>
            <button 
              onClick={() => setActiveTab('ROUTINES')}
              className={`text-[10px] font-black uppercase tracking-[0.3em] transition-all ${activeTab === 'ROUTINES' ? 'text-cyan-400' : 'text-white/20 hover:text-white'}`}
            >
              RUTINAS
            </button>
            <button 
              onClick={() => setActiveTab('HOME')}
              className={`text-[10px] font-black uppercase tracking-[0.3em] transition-all ${activeTab === 'HOME' ? 'text-cyan-400' : 'text-white/20 hover:text-white'}`}
            >
              CASA
            </button>
            <button 
              onClick={() => setActiveTab('COACHES')}
              className={`text-[10px] font-black uppercase tracking-[0.3em] transition-all ${activeTab === 'COACHES' ? 'text-cyan-400' : 'text-white/20 hover:text-white'}`}
            >
              ENTRENADORES
            </button>
            <button 
              onClick={() => setActiveTab('STORE')}
              className={`text-[10px] font-black uppercase tracking-[0.3em] transition-all ${activeTab === 'STORE' ? 'text-cyan-400' : 'text-white/20 hover:text-white'}`}
            >
              TIENDA
            </button>
            <button 
              onClick={() => setActiveTab('COMMUNITY')}
              className={`text-[10px] font-black uppercase tracking-[0.3em] transition-all ${activeTab === 'COMMUNITY' ? 'text-cyan-400' : 'text-white/20 hover:text-white'}`}
            >
              COMUNIDAD
            </button>
            <button 
              onClick={() => setActiveTab('CALCS')}
              className={`text-[10px] font-black uppercase tracking-[0.3em] transition-all ${activeTab === 'CALCS' ? 'text-cyan-400' : 'text-white/20 hover:text-white'}`}
            >
              CALCULADORAS
            </button>
            {profile?.role === 'admin' && (
              <button 
                onClick={() => setActiveTab('MASTER')}
                className={`text-[10px] font-black uppercase tracking-[0.3em] transition-all ${activeTab === 'MASTER' ? 'text-red-500' : 'text-red-500/40 hover:text-red-500'}`}
              >
                MASTER ADMIN
              </button>
            )}
          </nav>
        </div>

        <div className="flex items-center gap-6">
          <div 
            onClick={toggleLoyaltyCard}
            className="hidden sm:flex items-center gap-3 cursor-pointer group"
          >
            <div className="w-8 h-8 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center group-hover:bg-cyan-500 transition-all">
              <Star className="w-4 h-4 text-cyan-400 group-hover:text-black" />
            </div>
            <div className="text-left">
              <p className="text-[10px] font-black text-white leading-none uppercase">{loyaltyPoints} PTS</p>
            </div>
          </div>

          <button 
            onClick={() => setMode('HUB')}
            className="px-6 py-2 bg-white text-black text-[10px] font-black uppercase tracking-widest hover:bg-cyan-500 hover:text-white transition-all rounded-full"
          >
            SALIR
          </button>
        </div>
      </motion.div>

      <div className="flex-1 flex flex-col lg:flex-row gap-6 min-h-0 overflow-hidden">
        {/* Main Selection Area (Body Map & Controls) */}
        {activeTab === 'TRAINING' && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`bg-black/40 backdrop-blur-xl border border-white/5 rounded-2xl md:rounded-[2.5rem] p-4 md:p-10 pointer-events-auto flex flex-col gap-4 md:gap-8 shadow-xl overflow-hidden shrink-0 transition-all duration-500 ease-in-out relative ${
              selectedMuscle 
              ? 'w-full lg:w-[450px] h-fit lg:h-full hide-scrollbar' 
              : 'w-full max-w-6xl mx-auto lg:px-20 h-full'
            }`}
          >
            <div className="flex flex-col gap-6 h-full">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                   <h3 className="text-[10px] font-black text-cyan-400 uppercase tracking-[0.4em]">Selección de Músculo</h3>
                   {selectedMuscle && (
                     <button onClick={() => setSelectedMuscle(null)} className="text-[8px] font-black text-white/40 uppercase tracking-widest hover:text-white transition-colors">Limpiar</button>
                   )}
                </div>

                {/* Muscle Select Combo Box */}
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-transparent opacity-10 group-hover:opacity-20 blur transition-all rounded-2xl" />
                  <select 
                    value={selectedMuscle || ''} 
                    onChange={(e) => {
                      const muscle = e.target.value as MuscleGroup || null;
                      setSelectedMuscle(muscle);
                      // Auto-switch view if it makes sense
                      if (muscle === 'back' || muscle === 'glutes' || muscle === 'hamstrings' || muscle === 'calves' || muscle === 'lower_back' || muscle === 'triceps') {
                        setBodyView('back');
                      } else if (muscle) {
                        setBodyView('front');
                      }
                    }}
                    className="relative w-full bg-black/60 border border-white/10 rounded-2xl px-6 py-5 text-sm font-black uppercase tracking-[0.2em] text-white appearance-none outline-none focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all cursor-pointer shadow-2xl"
                  >
                    <option value="" className="bg-[#0a0a0a] text-white/40">--- ELIGE UN COMBO MUSCULAR ---</option>
                    {Object.entries(MUSCLE_NAMES).sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => (
                      <option key={id} value={id} className="bg-[#0a0a0a] text-white px-4 py-2">{name.toUpperCase()}</option>
                    ))}
                  </select>
                  <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-cyan-400 group-hover:scale-110 transition-transform">
                    <ChevronLeft className="w-5 h-5 rotate-[-90deg]" />
                  </div>
                </div>

                <div className="flex items-center justify-between py-6 border-y border-white/5">
                  <div className="flex bg-black/40 p-1.5 rounded-2xl border border-white/10 shrink-0 shadow-2xl">
                    <button 
                      onClick={() => setBodyView('front')}
                      className={`px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all ${bodyView === 'front' ? 'bg-cyan-500 text-black shadow-[0_0_20px_rgba(6,182,212,0.4)]' : 'text-white/40 hover:text-white'}`}
                    >
                      Frente
                    </button>
                    <button 
                      onClick={() => setBodyView('back')}
                      className={`px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all ${bodyView === 'back' ? 'bg-cyan-500 text-black shadow-[0_0_20px_rgba(6,182,212,0.4)]' : 'text-white/40 hover:text-white'}`}
                    >
                      Espalda
                    </button>
                  </div>

                  <div className="flex bg-white/5 p-1 rounded-xl border border-white/10 shrink-0">
                    <button 
                      onClick={() => setGender('male')}
                      className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${gender === 'male' ? 'bg-white text-black font-bold' : 'text-white/20'}`}
                    >
                      M
                    </button>
                    <button 
                      onClick={() => setGender('female')}
                      className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${gender === 'female' ? 'bg-white text-black font-bold' : 'text-white/20'}`}
                    >
                      F
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex-1 flex items-center justify-center p-4">
                <div className={`${selectedMuscle ? 'scale-50 lg:scale-90' : 'scale-75 lg:scale-100'} transition-all duration-700`}>
                  <BodyMap gender={gender} view={bodyView} onSelect={setSelectedMuscle} selectedMuscle={selectedMuscle} />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Content Area */}
        <div className="flex-1 flex flex-col gap-6 overflow-hidden">
          <AnimatePresence mode="wait">
            {activeTab === 'TRAINING' ? (
              <motion.div 
                key="training-view"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                className="h-full flex flex-col min-h-0"
              >
                {!selectedMuscle ? (
                  <div className="h-full flex flex-col p-4 md:p-8 lg:p-10 overflow-y-auto custom-scrollbar pointer-events-auto">
                    {/* Header with Title & Search */}
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
                      <div>
                        <h3 className="text-3xl md:text-4xl font-black italic uppercase tracking-tighter text-white">
                          SELECCIONA <span className="text-cyan-400">ZONA CORPORAL</span>
                        </h3>
                        <p className="text-white/40 text-[10px] md:text-xs font-medium uppercase tracking-[0.2em] mt-1">
                          Elige en el mapa interactivo o utiliza las tarjetas rápidas
                        </p>
                      </div>

                      {/* Search Bar */}
                      <div className="relative w-full md:w-80 group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400" />
                        <input 
                          type="text"
                          placeholder="BUSCAR MÚSCULO..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full bg-black/60 border border-white/10 rounded-full py-3.5 pl-11 pr-10 text-[10px] font-black uppercase tracking-widest outline-none focus:border-cyan-500/50 focus:ring-4 focus:ring-cyan-500/5 transition-all text-white placeholder:text-white/20 shadow-xl"
                        />
                        {searchQuery && (
                          <button 
                            onClick={() => setSearchQuery('')}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/20 hover:text-white transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Filter Category Tabs */}
                    <div className="flex gap-2 p-1 bg-black/40 rounded-2xl border border-white/5 mb-8 overflow-x-auto self-start shadow-xl hide-scrollbar">
                      {(['Todos', 'Superior', 'Inferior', 'Core'] as const).map((cat) => (
                        <button
                          key={cat}
                          onClick={() => setMuscleCategoryFilter(cat)}
                          className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all ${
                            muscleCategoryFilter === cat 
                            ? 'bg-cyan-500 text-black font-bold shadow-[0_0_15px_rgba(6,182,212,0.3)]' 
                            : 'text-white/40 hover:text-white hover:bg-white/5'
                          }`}
                        >
                          {cat === 'Todos' ? 'TODO' : cat === 'Superior' ? 'TREN SUPERIOR' : cat === 'Inferior' ? 'TREN INFERIOR' : 'CORE & LUMBAR'}
                        </button>
                      ))}
                    </div>

                    {/* Bento Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                      {MUSCLE_CARDS.filter(m => {
                        const matchesCategory = muscleCategoryFilter === 'Todos' || m.category === muscleCategoryFilter;
                        const matchesSearch = m.name.toLowerCase().includes(searchQuery.toLowerCase()) || m.description.toLowerCase().includes(searchQuery.toLowerCase());
                        return matchesCategory && matchesSearch;
                      }).map((mc) => {
                        const count = EXERCISES.filter(e => e.muscle === mc.id).length;
                        return (
                          <motion.button
                            key={mc.id}
                            whileHover={{ y: -4, scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                              setSelectedMuscle(mc.id);
                              setBodyView(mc.view);
                            }}
                            className="relative text-left bg-gradient-to-br from-white/[0.03] to-transparent hover:from-cyan-500/[0.06] hover:to-cyan-500/[0.01] border border-white/5 hover:border-cyan-500/30 rounded-2xl md:rounded-3xl p-6 transition-all group/card shadow-lg flex flex-col justify-between overflow-hidden min-h-[180px]"
                          >
                            <div className="absolute inset-0 bg-cyan-500/[0.01] opacity-0 group-hover/card:opacity-100 transition-opacity duration-500 pointer-events-none" />
                            
                            <div>
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/50 group-hover/card:bg-cyan-500/20 group-hover/card:border-cyan-400/30 group-hover/card:text-cyan-400 transition-all">
                                    <MuscleIcon name={mc.icon} className="w-4 h-4" />
                                  </div>
                                  <span className={`text-[8px] font-black uppercase tracking-widest px-2.5 py-1 rounded-md border ${
                                    mc.category === 'Superior' ? 'bg-indigo-500/10 border-indigo-500/20 text-indigo-300' :
                                    mc.category === 'Inferior' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300' :
                                    'bg-amber-500/10 border-amber-500/20 text-amber-300'
                                  }`}>
                                    {mc.category}
                                  </span>
                                </div>
                                <span className="text-[10px] font-black text-cyan-400 bg-cyan-500/10 px-2.5 py-1 rounded-lg border border-cyan-500/20">
                                  {count} EJERCICIOS
                                </span>
                              </div>
                              
                              <h4 className="text-xl font-black text-white italic uppercase tracking-tighter mb-2 group-hover/card:text-cyan-300 transition-colors">
                                {mc.name}
                              </h4>
                              
                              <p className="text-white/40 text-[11px] leading-relaxed line-clamp-2 uppercase font-medium">
                                {mc.description}
                              </p>
                            </div>

                            <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between">
                              <span className="text-[8px] font-black text-white/30 uppercase tracking-[0.2em] group-hover/card:text-cyan-400/70 transition-all">
                                Iniciar entrenamiento →
                              </span>
                              <div className="w-7 h-7 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/40 group-hover/card:bg-cyan-500 group-hover/card:text-black group-hover/card:border-cyan-400 group-hover/card:scale-110 transition-all">
                                <ArrowRight className="w-3.5 h-3.5" />
                              </div>
                            </div>
                          </motion.button>
                        );
                      })}
                      {MUSCLE_CARDS.filter(m => {
                        const matchesCategory = muscleCategoryFilter === 'Todos' || m.category === muscleCategoryFilter;
                        const matchesSearch = m.name.toLowerCase().includes(searchQuery.toLowerCase()) || m.description.toLowerCase().includes(searchQuery.toLowerCase());
                        return matchesCategory && matchesSearch;
                      }).length === 0 && (
                        <div className="col-span-full py-16 flex flex-col items-center justify-center text-center">
                          <p className="text-white/20 text-xs font-black uppercase tracking-widest mb-2">No se encontraron resultados</p>
                          <button onClick={() => { setSearchQuery(''); setMuscleCategoryFilter('Todos'); }} className="text-[10px] font-black text-cyan-400 uppercase tracking-widest hover:underline">Restablecer filtros</button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : selectedExercise ? (
                  <motion.div
                    key="details"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="h-full bg-black/40 backdrop-blur-xl border border-white/5 rounded-xl md:rounded-[2rem] p-4 md:p-10 lg:p-16 overflow-hidden flex flex-col pointer-events-auto shadow-xl relative"
                  >
                    <div className="flex-1 overflow-y-auto pr-2 md:pr-8 custom-scrollbar pb-20">
                      <button 
                        onClick={() => setSelectedExercise(null)}
                        className="flex items-center gap-2 text-cyan-400 text-[8px] md:text-[10px] font-black uppercase tracking-[0.4em] mb-6 md:mb-16 hover:translate-x-[-8px] transition-transform group"
                      >
                        <ChevronLeft className="w-4 h-4 group-hover:scale-125 transition-transform" /> Volver
                      </button>
                      
                      <div className="flex flex-col xl:flex-row gap-8 xl:gap-20">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-6 border-b border-white/5 pb-6">
                            <span className="px-3 md:px-5 py-1.5 md:py-2 bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-[8px] md:text-[10px] font-black uppercase tracking-[0.2em] rounded-full">
                               {MUSCLE_NAMES[selectedExercise.muscle]}
                            </span>
                            <span className="px-3 md:px-5 py-1.5 md:py-2 bg-white/5 border border-white/10 text-white/40 text-[8px] md:text-[10px] font-black uppercase tracking-[0.2em] rounded-full">
                               {selectedExercise.difficulty}
                            </span>
                            <span className="px-3 md:px-5 py-1.5 md:py-2 bg-white/5 border border-white/10 text-white/60 text-[8px] md:text-[10px] font-black uppercase tracking-[0.2em] rounded-full flex items-center gap-2">
                               <Dumbbell className="w-2.5 h-2.5 text-cyan-400/50" /> {selectedExercise.equipment}
                            </span>
                          </div>
                          
                          <h2 className="text-3xl md:text-6xl lg:text-8xl font-black text-white italic uppercase tracking-tighter mb-8 md:mb-12 leading-none">{selectedExercise.name}</h2>
                          
                          {/* AI Coach Trigger */}
                          <div className="mb-12">
                             {!showAiTip ? (
                               <motion.button
                                 whileHover={{ scale: 1.02 }}
                                 whileTap={{ scale: 0.98 }}
                                 onClick={() => fetchAiCoachTip(selectedExercise)}
                                 className="w-full relative group overflow-hidden rounded-3xl p-px"
                               >
                                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500 animate-shimmer group-hover:opacity-100 transition-opacity" />
                                  <div className="relative bg-black/60 backdrop-blur-2xl px-8 py-6 rounded-[23px] flex items-center justify-between border border-white/5 group-hover:border-transparent transition-all">
                                     <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20 group-hover:bg-cyan-500 group-hover:text-black transition-all">
                                           <BrainCircuit className="w-6 h-6 text-cyan-400 group-hover:text-black" />
                                        </div>
                                        <div className="text-left">
                                           <p className="text-[10px] font-black text-cyan-400 uppercase tracking-[0.4em]">Optimización Biofísica</p>
                                           <h4 className="text-xl font-black text-white italic uppercase tracking-tighter">Consultar AI Coach</h4>
                                        </div>
                                     </div>
                                     <Sparkles className="w-5 h-5 text-cyan-400 animate-pulse" />
                                  </div>
                               </motion.button>
                             ) : (
                               <motion.div 
                                 initial={{ opacity: 0, y: 20 }}
                                 animate={{ opacity: 1, y: 0 }}
                                 className="bg-cyan-500/10 border border-cyan-400/30 rounded-3xl p-6 md:p-8 relative overflow-hidden group"
                               >
                                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                                     <Microscope className="w-20 h-20 text-cyan-400" />
                                  </div>
                                  <div className="relative z-10">
                                     <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                           <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                                           <p className="text-[10px] font-black text-cyan-400 uppercase tracking-[0.4em]">Análisis Bio-Mecánico</p>
                                        </div>
                                        <button 
                                          onClick={() => setShowAiTip(false)}
                                          className="text-white/20 hover:text-white transition-colors"
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                     </div>
                                     <p className={`text-lg md:text-2xl font-black italic tracking-tight text-white uppercase leading-tight ${isAiCoaching ? 'animate-pulse text-white/50' : ''}`}>
                                        "{aiTip}"
                                     </p>
                                  </div>
                               </motion.div>
                             )}
                          </div>

                          <div className="space-y-10 xl:space-y-16">
                            <div>
                               <h3 className="text-[8px] md:text-[12px] font-black text-white/20 uppercase tracking-[0.5em] mb-6 md:mb-10 flex items-center gap-3">
                                  <Info className="w-4 h-4 text-cyan-400" /> Método Profesional
                               </h3>
                               <div className="space-y-4 md:space-y-8">
                                 {selectedExercise.instructions.map((step, i) => (
                                   <div key={i} className="flex gap-4 md:gap-8 group">
                                     <div className="shrink-0 w-8 h-8 md:w-12 md:h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[10px] md:text-sm font-black text-white group-hover:bg-cyan-500 group-hover:text-black transition-all duration-300">
                                       {i + 1}
                                     </div>
                                     <div className="pt-1.5 md:pt-3">
                                       <p className="text-white/60 text-sm md:text-lg leading-relaxed group-hover:text-white transition-colors uppercase tracking-tight font-medium">{step}</p>
                                     </div>
                                   </div>
                                 ))}
                               </div>
                            </div>
                          </div>
                        </div>

                        <div className="w-full xl:w-[450px] shrink-0 space-y-6 md:space-y-10 pb-8 xl:pb-0">
                          <div className="aspect-video xl:aspect-square bg-black/40 rounded-2xl md:rounded-3xl border border-white/5 overflow-hidden relative group shadow-2xl">
                             {selectedExercise.image ? (
                               <img src={selectedExercise.image} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" alt={selectedExercise.name} />
                             ) : (
                               <>
                                 <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(6,182,212,0.1)_0%,transparent_70%)]" />
                                 <div className="absolute inset-0 flex flex-col items-center justify-center p-8 md:p-12 text-center">
                                    <motion.div
                                       animate={{ scale: [1, 1.05, 1] }}
                                       transition={{ duration: 4, repeat: Infinity }}
                                       className="w-16 h-16 md:w-24 md:h-24 bg-cyan-500/10 rounded-full flex items-center justify-center mb-6 md:mb-10 border border-cyan-500/20"
                                    >
                                       <Dumbbell className="w-8 h-8 md:w-10 md:h-10 text-cyan-400" />
                                    </motion.div>
                                    <p className="text-white/20 text-[10px] md:text-xs font-black uppercase tracking-[0.6em] mb-4">Visualización de Técnica</p>
                                    <p className="text-white/40 text-[9px] md:text-[10px] uppercase font-bold leading-relaxed px-4 md:px-8">Sistema de guía anatómica activado</p>
                                 </div>
                               </>
                             )}
                             <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 pointer-events-none" />
                             <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2">
                                <Button variant="outline" className="border-white/10 text-white/60 bg-black/40 hover:bg-cyan-500 hover:text-black rounded-full px-6 md:px-8 py-5 md:py-6 text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap">
                                    <Zap className="w-3 h-3 mr-2 animate-pulse" /> Ver Tutorial Gemma 4 HD
                                </Button>
                             </div>
                          </div>
                          
                          <Button 
                            onClick={() => addPoints(25)}
                            className="w-full py-8 md:py-10 bg-cyan-500 hover:bg-cyan-400 text-black font-black uppercase tracking-[0.4em] text-[10px] md:text-xs rounded-2xl md:rounded-3xl shadow-[0_30px_60px_rgba(6,182,212,0.3)] transition-all transform hover:scale-[1.02] active:scale-95"
                          >
                            MARCAR COMO COMPLETADO (+25 PTS)
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <div className="flex-1 flex flex-col pointer-events-auto min-h-0">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-6 mb-4 md:mb-8 border-b border-white/5 pb-6 md:pb-10">
                       <motion.div
                         initial={{ opacity: 0, scale: 0.9 }}
                         animate={{ opacity: 1, scale: 1 }}
                         className="flex-1"
                       >
                          <div className="flex items-center gap-3 mb-2 md:mb-4">
                            <p className="text-[8px] md:text-[10px] font-black text-cyan-400 uppercase tracking-[0.5em]">Listado de entrenamiento</p>
                            <div className="h-px bg-cyan-500/20 flex-1 hidden md:block" />
                          </div>
                          <h2 className="text-3xl md:text-8xl font-black text-white italic uppercase tracking-tighter leading-none">
                            {MUSCLE_NAMES[selectedMuscle]}
                          </h2>
                       </motion.div>
                       
                       <div className="flex flex-col gap-2 md:gap-4 items-start md:items-end">
                         <button 
                           onClick={() => { setSelectedMuscle(null); setSelectedEquipment('Todos'); setSelectedDifficulty('Todos'); }}
                           className="flex items-center gap-2 md:gap-4 text-white/20 hover:text-cyan-400 text-[8px] md:text-[10px] font-black uppercase tracking-widest transition-all mb-1 group w-fit"
                         >
                           <ChevronLeft className="w-3 h-3 group-hover:-translate-x-2 transition-transform" /> Cambiar Zona
                         </button>
                         
                         <div className="flex flex-wrap gap-1.5 md:gap-2 justify-start md:justify-end">
                            {/* Equipment Filter */}
                            <select 
                              value={selectedEquipment} 
                              onChange={(e) => setSelectedEquipment(e.target.value)}
                              className="bg-black/60 border border-white/10 rounded-full px-3 py-1.5 text-[7px] md:text-[8px] font-black uppercase tracking-widest text-white/60 outline-none focus:border-cyan-500 transition-colors cursor-pointer"
                            >
                               <option value="Todos">Equipamiento</option>
                               <option value="Mancuerna">Mancuerna</option>
                               <option value="Barra">Barra</option>
                               <option value="Peso Corporal">Libre</option>
                               <option value="Máquina">Máquina</option>
                               <option value="Cable">Cable</option>
                            </select>
                            
                            {/* Difficulty Filter */}
                            <select 
                              value={selectedDifficulty} 
                              onChange={(e) => setSelectedDifficulty(e.target.value)}
                              className="bg-black/60 border border-white/10 rounded-full px-3 py-1.5 text-[7px] md:text-[8px] font-black uppercase tracking-widest text-white/60 outline-none focus:border-cyan-500 transition-colors cursor-pointer"
                            >
                               <option value="Todos">Dificultad</option>
                               <option value="Principiante">Basic</option>
                               <option value="Intermedio">Mid</option>
                               <option value="Avanzado">Pro</option>
                            </select>
                         </div>
                       </div>
                    </div>

                    <div className="flex-1 overflow-y-auto pr-1 md:pr-6 custom-scrollbar pb-24 md:pb-32">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-8">
                        {filteredExercises.map((ex, idx) => (
                          <motion.button
                            key={ex.id}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                            onClick={() => setSelectedExercise(ex)}
                            className="group bg-black/40 border border-white/5 rounded-3xl p-0 text-left hover:border-cyan-500/40 transition-all duration-500 relative overflow-hidden"
                          >
                            <div className="aspect-video relative overflow-hidden">
                              <img src={ex.image || "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=800&auto=format&fit=crop"} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" alt={ex.name} />
                              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
                              <div className="absolute top-4 right-4">
                                <div className="px-3 py-1 bg-black/60 backdrop-blur-xl rounded-full border border-white/10 text-[8px] font-black text-white uppercase tracking-widest">
                                  {ex.difficulty}
                                </div>
                              </div>
                            </div>
                            
                            <div className="p-6">
                              <h3 className="text-xl md:text-2xl font-black text-white italic uppercase tracking-tighter mb-2 group-hover:text-cyan-400 transition-colors leading-none">{ex.name}</h3>
                              <p className="text-white/30 text-[9px] uppercase font-bold tracking-widest mb-4 flex items-center gap-2">
                                <Dumbbell className="w-3 h-3 text-cyan-500" /> {ex.equipment}
                              </p>
                              
                              <div className="flex items-center justify-between pt-4 border-t border-white/5">
                                <span className="text-[8px] font-black text-white/10 uppercase tracking-[0.3em]">Muscle Tech</span>
                                <div className="flex items-center gap-2 text-cyan-400 text-[10px] font-black uppercase tracking-widest">
                                  Ver Técnica <ArrowRight className="w-3 h-3" />
                                </div>
                              </div>
                            </div>
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ) : activeTab === 'HOME' ? (
              <motion.div
                key="home-view"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.05 }}
                className="h-full flex flex-col pointer-events-auto min-h-0"
              >
                <div className="mb-12">
                   <h2 className="text-4xl md:text-6xl font-black text-white italic uppercase tracking-tighter mb-2">Entrenar <span className="text-cyan-400">en Casa</span></h2>
                   <p className="text-white/40 text-[10px] font-black uppercase tracking-[0.4em]">Sin excusas, sin equipo, resultados reales</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 overflow-y-auto pr-2 custom-scrollbar pb-12">
                  {routinesList.filter(r => r.category === 'Casa').map((routine) => (
                    <button
                      key={routine.id}
                      onClick={() => setSelectedRoutine(routine)}
                      className="group relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-[2.5rem] overflow-hidden text-left hover:border-cyan-500/50 transition-all"
                    >
                      <div className="aspect-video relative overflow-hidden">
                         <img src={routine.image} className="w-full h-full object-cover grayscale transition-all duration-700 group-hover:scale-110 group-hover:grayscale-0" alt={routine.name} />
                         <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black to-transparent" />
                         <div className="absolute top-6 right-6 flex gap-2">
                            <span className="px-3 py-1 bg-cyan-500 text-black rounded-full text-[8px] font-black uppercase tracking-widest">
                              CASA
                            </span>
                            <span className="px-3 py-1 bg-black/60 backdrop-blur-xl rounded-full text-[8px] font-black text-white uppercase tracking-widest border border-white/10">
                              {routine.difficulty}
                            </span>
                         </div>
                      </div>
                      <div className="p-8">
                         <h3 className="text-2xl font-black text-white italic uppercase tracking-tighter mb-2 group-hover:text-cyan-400 transition-colors">{routine.name}</h3>
                         <p className="text-white/40 text-[10px] leading-relaxed mb-6 uppercase tracking-tight">{routine.description}</p>
                         <div className="flex items-center justify-between pt-6 border-t border-white/5">
                            <div className="flex items-center gap-3">
                               <Clock className="w-3 h-3 text-cyan-400" />
                               <span className="text-[10px] font-black text-white/60 uppercase tracking-widest">{routine.duration}</span>
                            </div>
                            <div className="flex items-center gap-2 text-cyan-400 text-[10px] font-black uppercase tracking-widest translate-x-[-10px] opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all">
                               Ver Plan <ArrowRight className="w-3 h-3" />
                            </div>
                         </div>
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            ) : activeTab === 'COACHES' ? (
              showcaseTrainerId ? (
                // Trainer Showcase (Vitrina de Coach)
                <motion.div
                  key="trainer-showcase-view"
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  className="h-full flex flex-col pointer-events-auto min-h-0 bg-black/45 backdrop-blur-3xl border border-white/5 rounded-[2.5rem] p-6 md:p-10 overflow-hidden"
                >
                  {/* Top Navigation Row */}
                  <div className="flex justify-between items-center mb-8 border-b border-white/5 pb-6">
                    <button
                      onClick={() => setShowcaseTrainerId(null)}
                      className="flex items-center gap-3 px-5 py-3.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-[10px] font-black uppercase tracking-widest text-white transition-all cursor-pointer shadow-lg"
                    >
                      <ChevronLeft className="w-4 h-4 text-cyan-400" />
                      Volver a Coaches
                    </button>
                    <div>
                      <span className="text-[10px] font-black text-white/30 uppercase tracking-[0.4em]">VITRINA DIGITAL COMPLETA</span>
                    </div>
                  </div>

                  {/* Showcase Content Layout */}
                  {(() => {
                    const trainer = trainersList.find(t => t.id === showcaseTrainerId);
                    if (!trainer) return <p className="text-white/60">Entrenador no encontrado.</p>;

                    const trainerRoutines = routinesList.filter(r => r.trainerId === trainer.id);
                    const trainerProducts = productsList.filter(p => p.trainerId === trainer.id);
                    const isMyCoach = selectedTrainer?.id === trainer.id;

                    return (
                      <div className="flex-1 flex flex-col lg:flex-row gap-8 overflow-hidden min-h-0">
                        {/* Left Column: Trainer Card & Actions */}
                        <div className="w-full lg:w-1/3 flex flex-col gap-6 overflow-y-auto pr-1">
                          <div className="bg-black/60 rounded-[2rem] border border-white/15 overflow-hidden shadow-2xl relative">
                            {/* Premium Glow Aspect Frame */}
                            <div className="aspect-[4/5] relative overflow-hidden">
                              <img src={trainer.image} className="w-full h-full object-cover grayscale-0 scale-100" alt={trainer.name} />
                              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                              
                              <div className="absolute bottom-6 left-6 right-6">
                                <span className="text-[9px] font-black text-cyan-400 uppercase tracking-[0.4em] mb-1.5 block">ESPECIALISTA</span>
                                <h3 className="text-3xl md:text-4xl font-black text-white italic uppercase tracking-tighter leading-tight">{trainer.name}</h3>
                                <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest mt-1 flex items-center gap-2">
                                  <Target className="w-3.5 h-3.5 text-cyan-400" /> {trainer.experience}
                                </p>
                              </div>
                            </div>
                            
                            <div className="p-6 space-y-6">
                              <div>
                                <h4 className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-2">Filosofía de Entrenamiento</h4>
                                <p className="text-white/70 text-xs leading-relaxed uppercase tracking-tight">{trainer.bio}</p>
                              </div>

                              {/* Trainer Rating Block */}
                              <div className="flex items-center gap-4 py-4 border-y border-white/5">
                                <div className="flex items-center gap-1.5">
                                  <Star className="w-3.5 h-3.5 text-cyan-400 fill-cyan-400" />
                                  <Star className="w-3.5 h-3.5 text-cyan-400 fill-cyan-400" />
                                  <Star className="w-3.5 h-3.5 text-cyan-400 fill-cyan-400" />
                                  <Star className="w-3.5 h-3.5 text-cyan-400 fill-cyan-400" />
                                  <Star className="w-3.5 h-3.5 text-cyan-400 fill-cyan-400" />
                                </div>
                                <span className="text-[9px] font-black text-cyan-400 uppercase tracking-widest">5.0 RESEÑAS EXCELENTES</span>
                              </div>

                              {/* Interactive Optional Coach Selector */}
                              <div className="space-y-3">
                                {isMyCoach ? (
                                  <div className="space-y-3">
                                    <div className="w-full bg-cyan-500/20 border border-cyan-500 text-cyan-300 font-black text-center uppercase tracking-widest py-4 rounded-xl text-[10px] shadow-[0_0_20px_rgba(6,182,212,0.15)] flex items-center justify-center gap-2">
                                      <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                                      Tu Entrenador de Cabecera
                                    </div>
                                    <Button
                                      onClick={() => setSelectedTrainer(null)}
                                      className="w-full bg-transparent hover:bg-red-500/10 text-red-400 hover:text-red-300 border border-red-500/30 font-black uppercase tracking-widest py-5 rounded-xl text-[9px] transition-all cursor-pointer"
                                    >
                                      Desvincular Coach (Entrenar Solo)
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    onClick={() => setSelectedTrainer(trainer)}
                                    className="w-full bg-white text-black hover:bg-cyan-500 hover:text-white font-black uppercase tracking-widest py-6 rounded-xl text-[9px] transition-all cursor-pointer shadow-lg"
                                  >
                                    Asignar como mi Coach Entrenador
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right Content Column: Routines & Merchandise Showcase */}
                        <div className="flex-1 flex flex-col gap-8 overflow-y-auto pr-2 custom-scrollbar pb-12">
                          {/* Signature Routines Section */}
                          <div>
                            <div className="flex items-center gap-3 mb-6">
                              <Dumbbell className="w-5 h-5 text-cyan-400" />
                              <h3 className="text-lg font-black uppercase tracking-tight italic">Rutinas Firmadas en Showcase</h3>
                            </div>

                            {trainerRoutines.length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {trainerRoutines.map((routine) => (
                                  <div key={routine.id} className="group bg-black/40 border border-white/10 rounded-2xl p-5 hover:border-cyan-500/50 transition-all flex gap-4 items-start">
                                    <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0">
                                      <img src={routine.image} className="w-full h-full object-cover transition-transform group-hover:scale-110" alt={routine.name} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-black text-sm uppercase tracking-tight group-hover:text-cyan-400 transition-colors truncate">{routine.name}</h4>
                                      <p className="text-[10px] text-white/50 line-clamp-2 mt-1 uppercase tracking-tight">{routine.description}</p>
                                      <div className="flex items-center gap-3 mt-3">
                                        <span className="text-[8px] font-bold text-cyan-400 uppercase tracking-widest bg-cyan-500/10 px-2 py-0.5 rounded-md border border-cyan-400/20">{routine.difficulty}</span>
                                        <span className="text-[8px] font-medium text-white/40 uppercase tracking-widest">{routine.duration}</span>
                                      </div>
                                      <Button
                                        size="sm"
                                        onClick={() => {
                                          setSelectedRoutine(routine);
                                          setActiveTab('ROUTINES');
                                        }}
                                        className="mt-4 bg-white/10 hover:bg-cyan-500 text-white hover:text-black font-black uppercase text-[8px] tracking-widest py-3.5 px-4 rounded-xl cursor-pointer transition-all"
                                      >
                                        Comenzar Rutina
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest py-4">Este entrenador no tiene rutinas asignadas actualmente.</p>
                            )}
                          </div>

                          {/* Recommended Products Showcase Section */}
                          <div>
                            <div className="flex items-center gap-3 mb-6">
                              <ShoppingBag className="w-5 h-5 text-cyan-400" />
                              <h3 className="text-lg font-black uppercase tracking-tight italic">E-Commerce: Recomendaciones de su Vitrina</h3>
                            </div>

                            {trainerProducts.length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {trainerProducts.map((p) => (
                                  <div key={p.id} className="group bg-black/40 border border-white/10 rounded-2xl overflow-hidden hover:border-cyan-500/50 transition-all flex flex-col">
                                    <div className="aspect-video relative overflow-hidden">
                                      <img src={p.image} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt={p.name} />
                                      <div className="absolute top-4 left-4 bg-cyan-900/80 backdrop-blur-md px-3 py-1 rounded-full text-[8.5px] font-black text-cyan-200 uppercase tracking-widest border border-cyan-500/30">
                                        RECOMENDADO POR {trainer.name.split(' ')[0].toUpperCase()}
                                      </div>
                                    </div>
                                    <div className="p-5 flex-1 flex flex-col">
                                      <h4 className="text-base font-black text-white uppercase italic tracking-tight group-hover:text-cyan-400 transition-colors">{p.name}</h4>
                                      <p className="text-white/40 text-[10px] mt-1.5 mb-5 uppercase tracking-tight line-clamp-2 leading-relaxed">{p.description}</p>
                                      
                                      <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between">
                                        <span className="text-xl font-black text-cyan-400">${p.price}</span>
                                        <Button
                                          onClick={() => {
                                            const ptsEarned = Math.floor(p.price * 10);
                                            addPoints(ptsEarned);
                                            setPurchaseSuccess({
                                              productName: p.name,
                                              price: p.price,
                                              pts: ptsEarned
                                            });
                                          }}
                                          size="sm"
                                          className="bg-white hover:bg-cyan-500 text-black hover:text-white font-black uppercase text-[9px] tracking-widest py-3.5 px-5 rounded-xl cursor-pointer transition-all"
                                        >
                                          Comprar
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest py-4">Este entrenador no tiene productos en vitrina actualmente.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </motion.div>
              ) : (
                // Coaches List (Vista general)
                <motion.div
                  key="coaches-view"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="h-full flex flex-col pointer-events-auto min-h-0"
                >
                  <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
                     <div>
                        <h2 className="text-4xl md:text-7xl font-black text-white italic uppercase tracking-tighter leading-none">Nuestros <span className="text-cyan-400">Coaches</span></h2>
                        <p className="text-white/30 text-[10px] font-black uppercase tracking-[0.5em] mt-2">Expertos en transformar tu realidad física</p>
                     </div>
                     <div className="flex bg-white/5 border border-white/10 p-2 rounded-2xl gap-4">
                        <div className="text-center px-4">
                          <p className="text-white font-black text-xl">15+</p>
                          <p className="text-white/20 text-[7px] uppercase font-black tracking-widest text-[8px]">Expertos</p>
                        </div>
                        <div className="w-px h-full bg-white/10" />
                        <div className="text-center px-4">
                          <p className="text-white font-black text-xl">5k+</p>
                          <p className="text-white/20 text-[7px] uppercase font-black tracking-widest text-[8px]">Alumnos</p>
                        </div>
                     </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 overflow-y-auto pr-2 custom-scrollbar pb-24">
                    {trainersList.map((trainer) => (
                      <motion.div
                        key={trainer.id}
                        whileHover={{ y: -10 }}
                        className="group bg-black/40 backdrop-blur-3xl border border-white/5 rounded-[2.5rem] overflow-hidden flex flex-col hover:border-cyan-500/30 transition-all duration-500"
                      >
                        <div className="aspect-[4/5] relative overflow-hidden">
                          <img src={trainer.image} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700" alt={trainer.name} />
                          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                          
                          <div className="absolute bottom-8 left-8 right-8">
                             <span className="text-[10px] font-black text-cyan-400 uppercase tracking-[0.4em] mb-2 block">{trainer.specialty}</span>
                             <h3 className="text-4xl font-black text-white italic uppercase tracking-tighter mb-2">{trainer.name}</h3>
                             <div className="flex items-center gap-2 text-white/40 text-[10px] font-bold uppercase tracking-widest">
                               <Target className="w-3 h-3" /> {trainer.experience}
                             </div>
                          </div>
                        </div>
                        <div className="p-8 flex flex-col flex-1">
                          <p className="text-white/60 text-xs leading-relaxed mb-8 uppercase font-medium tracking-tight h-12 line-clamp-3">
                            {trainer.bio}
                          </p>
                          <div className="mt-auto space-y-4">
                             <div className="flex items-center gap-4 py-4 border-t border-white/5">
                                <div className="flex items-center gap-2">
                                  <StarIcon className="w-4 h-4 text-cyan-400 fill-cyan-400" />
                                  <StarIcon className="w-4 h-4 text-cyan-400 fill-cyan-400" />
                                  <StarIcon className="w-4 h-4 text-cyan-400 fill-cyan-400" />
                                  <StarIcon className="w-4 h-4 text-cyan-400 fill-cyan-400" />
                                  <StarIcon className="w-4 h-4 text-cyan-400 fill-cyan-400" />
                                </div>
                                <span className="text-[9px] font-black text-white uppercase tracking-widest">4.9 / 5.0</span>
                             </div>
                             
                             <div className="grid grid-cols-2 gap-4">
                               <Button
                                 onClick={() => setShowcaseTrainerId(trainer.id)}
                                 className="w-full bg-cyan-500 text-black hover:bg-cyan-600 font-black uppercase text-[9px] tracking-widest py-6 rounded-2xl cursor-pointer transition-all"
                               >
                                 Ver Vitrina
                               </Button>
                               <Button
                                 onClick={() => {
                                   if (selectedTrainer?.id === trainer.id) {
                                     setSelectedTrainer(null);
                                   } else {
                                     setSelectedTrainer(trainer);
                                   }
                                 }}
                                 className={`w-full font-black uppercase text-[8.5px] tracking-widest py-6 rounded-2xl cursor-pointer transition-all ${
                                   selectedTrainer?.id === trainer.id 
                                     ? 'bg-red-500/20 text-red-400 border border-red-500 hover:bg-red-500 hover:text-white' 
                                     : 'bg-white text-black hover:bg-black hover:text-white hover:border hover:border-white/20'
                                 }`}
                               >
                                 {selectedTrainer?.id === trainer.id ? 'Vincular (Activo)' : 'Elegir Coach'}
                               </Button>
                             </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )
            ) : activeTab === 'ROUTINES' ? (
              <motion.div
                key="routines-view"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.05 }}
                className="h-full flex flex-col pointer-events-auto min-h-0"
              >
                {selectedRoutine ? (
                  <div className="bg-black/60 backdrop-blur-2xl border border-white/10 rounded-[1.5rem] md:rounded-3xl p-6 md:p-12 flex flex-col xl:flex-row gap-8 xl:gap-12 h-full overflow-hidden">
                    <div className="flex-1 flex flex-col min-h-0">
                      <button 
                         onClick={() => setSelectedRoutine(null)}
                         className="flex items-center gap-2 text-cyan-400 text-[10px] md:text-xs font-black uppercase tracking-[0.3em] mb-6 md:mb-8 hover:translate-x-[-4px] transition-transform w-fit"
                      >
                         <ChevronLeft className="w-4 h-4" /> Volver a Rutinas
                      </button>
                      <h2 className="text-3xl md:text-5xl font-black text-white italic uppercase tracking-tighter mb-4">{selectedRoutine.name}</h2>
                      <p className="text-white/40 text-[10px] md:text-sm mb-6 md:mb-8 max-w-xl uppercase tracking-tight">{selectedRoutine.description}</p>
                      
                      <div className="grid grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-8">
                         <div className="bg-white/5 border border-white/5 p-3 md:p-4 rounded-2xl">
                            <Clock className="w-3 md:w-4 h-3 md:h-4 text-cyan-400 mb-2" />
                            <p className="text-white text-[10px] md:text-xs font-bold uppercase">{selectedRoutine.duration}</p>
                         </div>
                         <div className="bg-white/5 border border-white/5 p-3 md:p-4 rounded-2xl">
                            <Zap className="w-3 md:w-4 h-3 md:h-4 text-cyan-400 mb-2" />
                            <p className="text-white text-[10px] md:text-xs font-bold uppercase">{selectedRoutine.difficulty}</p>
                         </div>
                      </div>

                      <div className="flex-1 overflow-y-auto pr-2 md:pr-4 custom-scrollbar">
                         <h3 className="text-white/20 font-black uppercase tracking-widest text-[10px] mb-4 md:mb-6">Bloque de Ejercicios</h3>
                         <div className="space-y-2 md:space-y-3">
                            {getRoutineExercises(selectedRoutine).map((ex, i) => (
                              <div key={ex.id} className="bg-white/5 border border-white/10 p-3 md:p-4 rounded-xl flex items-center justify-between group hover:bg-cyan-500/10 transition-colors">
                                 <div className="flex items-center gap-3 md:gap-4">
                                    <span className="text-[9px] md:text-[10px] font-black text-white/20 group-hover:text-cyan-400">{i+1}</span>
                                    <span className="text-xs md:text-sm font-bold text-white uppercase tracking-tight">{ex.name}</span>
                                 </div>
                                 <button 
                                   onClick={() => { setSelectedExercise(ex); setActiveTab('TRAINING'); }}
                                   className="text-[7px] md:text-[8px] font-black uppercase tracking-widest text-cyan-400 border border-cyan-500/20 px-2 md:px-3 py-1 rounded-full hover:bg-cyan-500 hover:text-black transition-colors"
                                 >
                                   Técnica
                                 </button>
                              </div>
                            ))}
                         </div>
                      </div>

                      <Button className="mt-6 md:mt-8 bg-cyan-500 hover:bg-cyan-400 text-black font-black uppercase tracking-widest py-5 md:py-6 rounded-none">
                         Iniciar Entrenamiento <Play className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                    <div className="w-full xl:w-96 h-48 xl:h-full shrink-0 rounded-3xl overflow-hidden relative">
                       <img src={selectedRoutine.image} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" alt={selectedRoutine.name} />
                       <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 overflow-y-auto pr-2 custom-scrollbar pb-12">
                    {routinesList.map((routine) => (
                      <button
                        key={routine.id}
                        onClick={() => setSelectedRoutine(routine)}
                        className="group relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-[2.5rem] overflow-hidden text-left hover:border-cyan-500/50 transition-all"
                      >
                        <div className="aspect-video relative overflow-hidden">
                           <img src={routine.image} className="w-full h-full object-cover grayscale transition-all duration-700 group-hover:scale-110 group-hover:grayscale-0" alt={routine.name} />
                           <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                           <div className="absolute top-6 right-6">
                              <span className="px-3 py-1 bg-black/60 backdrop-blur-xl rounded-full text-[8px] font-black text-white uppercase tracking-widest border border-white/10">
                                {routine.difficulty}
                              </span>
                           </div>
                        </div>
                        <div className="p-8">
                           <h3 className="text-2xl font-black text-white italic uppercase tracking-tighter mb-2 group-hover:text-cyan-400 transition-colors">{routine.name}</h3>
                           <p className="text-white/40 text-[10px] leading-relaxed mb-6 uppercase tracking-tight">{routine.description}</p>
                           <div className="flex items-center justify-between pt-6 border-t border-white/5">
                              <div className="flex items-center gap-3">
                                 <Clock className="w-3 h-3 text-cyan-400" />
                                 <span className="text-[10px] font-black text-white/60 uppercase tracking-widest">{routine.duration}</span>
                              </div>
                              <div className="flex items-center gap-2 text-cyan-400 text-[10px] font-black uppercase tracking-widest translate-x-[-10px] opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all">
                                 Detalles Rutina <ArrowRight className="w-3 h-3" />
                              </div>
                           </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            ) : activeTab === 'COMMUNITY' ? (
              <motion.div
                key="community-view"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="h-full flex flex-col pointer-events-auto min-h-0 bg-black/40 backdrop-blur-3xl border border-white/5 rounded-[2rem] p-8 md:p-12 overflow-y-auto custom-scrollbar"
              >
                <div className="max-w-6xl mx-auto w-full space-y-12 pb-20">
                {/* AI-Integrated Content Header */}
                <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-12">
                  <div className="text-center md:text-left">
                    <h2 className="text-4xl md:text-7xl font-black text-white italic uppercase tracking-tighter leading-none mb-2">Comunidad <span className="text-cyan-400">Gemma</span></h2>
                    <p className="text-white/30 text-[9px] font-black uppercase tracking-[0.5em]">Análisis Biomecánico impulsado por IA</p>
                  </div>
                  <div className="flex bg-cyan-500/10 border border-cyan-500/20 px-6 py-3 rounded-2xl items-center gap-3">
                     <div className="w-2 h-2 rounded-full bg-cyan-400 animate-ping" />
                     <span className="text-[10px] font-black uppercase tracking-widest text-cyan-400">Gemma 4 Global Sync: ON</span>
                  </div>
                </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    {/* Activity Feed Simulation */}
                    <div className="space-y-8">
                      <h3 className="text-white/20 text-[10px] font-black uppercase tracking-widest border-b border-white/5 pb-4">Social Feed</h3>
                      {[
                        { user: 'Marcos R.', action: 'completó Back Day', time: 'hace 5 min', pts: '+50' },
                        { user: 'Elena V.', action: 'se unió a Running Club', time: 'hace 15 min', pts: '+10' },
                        { user: 'Dani K.', action: 'alcanzó Nivel 3', time: 'hace 2h', pts: '+200' }
                      ].map((post, i) => (
                        <div key={i} className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-2xl group hover:bg-white/10 transition-all">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400">
                              <User className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-sm font-black text-white italic tracking-tight">{post.user} <span className="text-white/40 font-normal not-italic">{post.action}</span></p>
                              <p className="text-[9px] text-white/20 uppercase font-bold mt-1">{post.time}</p>
                            </div>
                          </div>
                          <span className="text-xs font-black text-cyan-400">{post.pts} PTS</span>
                        </div>
                      ))}
                      <button className="w-full py-4 border border-white/5 text-white/20 text-[9px] font-black uppercase tracking-widest hover:text-white transition-colors">
                        Ver más actividad
                      </button>
                    </div>

                    {/* Activities List */}
                    <div className="space-y-8">
                      <h3 className="text-white/20 text-[10px] font-black uppercase tracking-widest border-b border-white/5 pb-4">Actividades del Gym</h3>
                      <div className="grid grid-cols-1 gap-4">
                        {productsList.filter(p => p.category === 'Comunidad').map(activity => (
                          <div key={activity.id} className="group bg-black/40 border border-white/10 rounded-3xl overflow-hidden flex h-32 md:h-40 hover:border-cyan-500/50 transition-all">
                            <div className="w-1/3 h-full relative">
                              <img src={activity.image} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" alt={activity.name} />
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black" />
                            </div>
                            <div className="flex-1 p-4 md:p-6 flex flex-col justify-center">
                              <h4 className="text-lg md:text-xl font-black text-white uppercase italic tracking-tighter group-hover:text-cyan-400 transition-colors leading-none mb-2">{activity.name}</h4>
                              <p className="text-white/30 text-[9px] md:text-[10px] line-clamp-2 uppercase font-bold tracking-tight mb-4">{activity.description}</p>
                              <div className="flex items-center justify-between">
                                <span className={`text-[10px] font-black uppercase tracking-widest ${activity.price === 0 ? 'text-cyan-400' : 'text-white/60'}`}>
                                  {activity.price === 0 ? 'Gratis para Socios' : `$${activity.price}`}
                                </span>
                                <Button size="sm" className="bg-white text-black font-black uppercase text-[8px] tracking-widest h-7 px-4 hover:bg-cyan-500 hover:text-white rounded-none">
                                  Unirse
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : activeTab === 'CALCS' ? (
              <motion.div
                key="calcs-view"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.05 }}
                className="h-full flex flex-col pointer-events-auto min-h-0 bg-black/40 backdrop-blur-3xl border border-white/10 rounded-[2rem] p-8 md:p-12 overflow-y-auto custom-scrollbar"
              >
                <div className="max-w-4xl mx-auto w-full space-y-12 pb-20">
                  <div className="text-center">
                    <h2 className="text-4xl md:text-6xl font-black text-white italic uppercase tracking-tighter mb-4">Herramientas <span className="text-cyan-400">Pro</span></h2>
                    <p className="text-white/40 text-[10px] font-black uppercase tracking-[0.5em]">Optimiza cada gramo y cada repetición</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Macronutrient Calculator */}
                    <div className="bg-white/5 border border-white/10 rounded-3xl p-8 space-y-8">
                      <div className="flex items-center gap-4">
                         <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 flex items-center justify-center">
                            <Target className="w-6 h-6 text-cyan-400" />
                         </div>
                         <div>
                            <h3 className="text-xl font-black text-white uppercase italic tracking-wider">Macros & Calorías</h3>
                            <p className="text-white/20 text-[8px] font-black uppercase tracking-widest leading-none mt-1">Basado en tu biotipo</p>
                         </div>
                      </div>

                      <div className="space-y-6">
                         <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                               <label className="text-[8px] font-black uppercase text-white/30 tracking-widest">Peso (kg)</label>
                               <input type="number" value={weight} onChange={(e) => setWeight(Number(e.target.value))} className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-white font-black text-center outline-none focus:border-cyan-500/50" />
                            </div>
                            <div className="space-y-2">
                               <label className="text-[8px] font-black uppercase text-white/30 tracking-widest">Altura (cm)</label>
                               <input type="number" value={height} onChange={(e) => setHeight(Number(e.target.value))} className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-white font-black text-center outline-none focus:border-cyan-500/50" />
                            </div>
                            <div className="space-y-2">
                               <label className="text-[8px] font-black uppercase text-white/30 tracking-widest">Edad</label>
                               <input type="number" value={age} onChange={(e) => setAge(Number(e.target.value))} className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-white font-black text-center outline-none focus:border-cyan-500/50" />
                            </div>
                         </div>

                         <div className="flex bg-black/40 p-1 rounded-xl border border-white/10">
                            {(['cut', 'maintain', 'bulk'] as const).map((g) => (
                              <button 
                                key={g}
                                onClick={() => setGoal(g)}
                                className={`flex-1 py-3 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all ${goal === g ? 'bg-cyan-500 text-black' : 'text-white/40 hover:text-white'}`}
                              >
                                {g === 'cut' ? 'Perder' : g === 'bulk' ? 'Ganar' : 'Mantener'}
                              </button>
                            ))}
                         </div>
                      </div>

                      <div className="pt-6 border-t border-white/5 grid grid-cols-3 gap-4 text-center">
                         <div className="space-y-1">
                            <p className="text-[7px] font-black text-white/20 uppercase tracking-widest">Proteína</p>
                            <p className="text-xl font-black text-white italic">{weight * 2}g</p>
                         </div>
                         <div className="space-y-1">
                            <p className="text-[7px] font-black text-white/20 uppercase tracking-widest">Grasas</p>
                            <p className="text-xl font-black text-white italic">{Math.round(weight * 0.8)}g</p>
                         </div>
                         <div className="space-y-1">
                            <p className="text-[7px] font-black text-white/20 uppercase tracking-widest">Kcal</p>
                            <p className="text-xl font-black text-cyan-400 italic">2850</p>
                         </div>
                      </div>
                    </div>

                    {/* 1RM Calculator */}
                    <div className="bg-white/5 border border-white/10 rounded-3xl p-8 space-y-8">
                      <div className="flex items-center gap-4">
                         <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 flex items-center justify-center">
                            <Zap className="w-6 h-6 text-cyan-400" />
                         </div>
                         <div>
                            <h3 className="text-xl font-black text-white uppercase italic tracking-wider">Potencia 1RM</h3>
                            <p className="text-white/20 text-[8px] font-black uppercase tracking-widest leading-none mt-1">Calcula tu fuerza máxima</p>
                         </div>
                      </div>

                      <div className="space-y-6">
                         <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                               <label className="text-[8px] font-black uppercase text-white/30 tracking-widest">Peso Levantado</label>
                               <input type="number" defaultValue={100} className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-white font-black text-center outline-none focus:border-cyan-500/50" />
                            </div>
                            <div className="space-y-2">
                               <label className="text-[8px] font-black uppercase text-white/30 tracking-widest">Repeticiones</label>
                               <input type="number" defaultValue={5} className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-white font-black text-center outline-none focus:border-cyan-500/50" />
                            </div>
                         </div>
                      </div>

                      <div className="pt-6 border-t border-white/5 space-y-4">
                         <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black text-white/40 uppercase tracking-widest">Tu 1RM Estimado:</span>
                            <span className="text-4xl font-black text-cyan-400 italic">116 KG</span>
                         </div>
                         <div className="grid grid-cols-3 gap-2">
                            {[90, 80, 70].map(pct => (
                               <div key={pct} className="bg-black/20 p-2 rounded-lg text-center">
                                  <p className="text-[7px] font-black text-white/20 uppercase">{pct}%</p>
                                  <p className="text-xs font-black text-white italic">{Math.round(116 * pct / 100)}kg</p>
                               </div>
                            ))}
                         </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : activeTab === 'WIKI' ? (
              <motion.div
                key="wiki-view"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="h-full flex flex-col pointer-events-auto min-h-0 bg-black/20 backdrop-blur-3xl border border-white/5 rounded-[2rem] p-6 md:p-10 overflow-hidden"
              >
                <div className="flex flex-col lg:flex-row gap-8 h-full overflow-hidden">
                  {/* Left Column: List of 14 Muscle Groups */}
                  <div className="w-full lg:w-1/3 flex flex-col gap-6 overflow-hidden h-full">
                    <div className="space-y-1">
                      <h2 className="text-4xl md:text-5xl font-black text-white italic uppercase tracking-tighter">Guía <span className="text-red-500">Muscular</span></h2>
                      <p className="text-white/40 text-[9px] font-black uppercase tracking-[0.4em]">Enciclopedia Técnica e Ilustrada v1.2</p>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-3">
                      {Object.values(muscleWikiData.muscle_groups).map((group) => {
                        const isSelected = selectedWikiMuscle === group.id;
                        return (
                          <button
                            key={group.id}
                            onClick={() => setSelectedWikiMuscle(group.id)}
                            className={`w-full border p-5 rounded-2xl text-left group transition-all duration-300 cursor-pointer ${
                              isSelected
                                ? 'bg-red-500/10 border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.15)]'
                                : 'bg-white/5 border-white/10 hover:bg-red-500/5 hover:border-red-500/30'
                            }`}
                          >
                            <div className="flex justify-between items-center mb-1.5">
                               <h4 className={`text-lg font-black uppercase italic tracking-tighter transition-colors ${isSelected ? 'text-red-400' : 'text-white group-hover:text-red-400'}`}>{group.name}</h4>
                               <span className="text-[9px] font-black text-white/30 uppercase tracking-widest">{group.exercises_count} EX</span>
                            </div>
                            <p className="text-[8.5px] text-white/45 uppercase font-black tracking-widest">{group.name_en}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Right Column: Detailed Guide & Exercises List for the selected Muscle */}
                  {(() => {
                    const wikiGroup = (muscleWikiData.muscle_groups as any)[selectedWikiMuscle];
                    if (!wikiGroup) return <div className="flex-1 text-white/50">Elige un músculo del panel lateral.</div>;

                    // Map wiki muscle ID to our gym's MuscleGroup type
                    const mapWikiToGym = (wikiId: string): MuscleGroup | null => {
                      switch(wikiId) {
                        case 'pectorals': return 'chest';
                        case 'lats': return 'back';
                        case 'trapezius': return 'shoulders';
                        case 'deltoids': return 'shoulders';
                        case 'biceps': return 'biceps';
                        case 'triceps': return 'triceps';
                        case 'abs': return 'core';
                        case 'quadriceps': return 'quads';
                        case 'hamstrings': return 'hamstrings';
                        case 'glutes': return 'glutes';
                        case 'calves': return 'calves';
                        case 'rhomboids': return 'back';
                        case 'erector_spinae': return 'lower_back';
                        case 'forearms': return 'forearms';
                        default: return null;
                      }
                    };

                    const matchedGymMuscle = mapWikiToGym(selectedWikiMuscle);

                    // 1. Get exercises from the global interactive 3D exercise database matching this muscle
                    const interactiveExercises = EXERCISES.filter(e => e.muscle === matchedGymMuscle);

                    // 2. Fetch standard base exercises from the muscle-wiki JSON dataset
                    const staticWikiExercises: any[] = [];
                    Object.values(muscleWikiData.exercises).forEach((cat: any) => {
                      cat.exercises.forEach((ex: any) => {
                        if (ex.target_muscles.includes(selectedWikiMuscle)) {
                          staticWikiExercises.push({
                            id: ex.id,
                            name: ex.name,
                            equipment: cat.category,
                            difficulty: ex.difficulty === 'beginner' ? 'Principiante' : ex.difficulty === 'intermediate' ? 'Intermedio' : 'Avanzado'
                          });
                        }
                      });
                    });

                    return (
                      <div className="flex-1 bg-black/40 rounded-[2rem] border border-white/5 p-6 md:p-8 flex flex-col overflow-hidden min-h-0">
                        {/* Anatomical Card Header */}
                        <div className="border-b border-white/5 pb-5 mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                          <div>
                            <span className="text-[9px] font-black text-red-500 uppercase tracking-[0.4em] mb-1 block">ANATOMÍA ACTIVA</span>
                            <h3 className="text-3xl md:text-4xl font-black text-white italic uppercase tracking-tighter leading-none">{wikiGroup.name}</h3>
                            <p className="text-white/40 text-[9.5px] font-bold uppercase tracking-widest mt-1">Foco de Reclutamiento Muscular • {wikiGroup.name_en}</p>
                          </div>
                          <div className="px-4 py-2 bg-red-500/15 border border-red-500/30 rounded-xl">
                            <span className="text-[9px] font-black text-red-400 uppercase tracking-widest">LOCALIZACIÓN: {wikiGroup.location.replace('_', ' ').toUpperCase()}</span>
                          </div>
                        </div>

                        {/* Description Box */}
                        <div className="bg-white/[0.02] border border-white/5 p-4 rounded-xl mb-6 relative overflow-hidden shrink-0">
                          <div className="absolute right-3 top-3 pointer-events-none opacity-20">
                            <Info className="w-5 h-5 text-red-400" />
                          </div>
                          <h4 className="text-[8px] font-black text-white/30 uppercase tracking-widest mb-1">Trazo Biomecánico Continuo</h4>
                          <p className="text-[11px] text-white/70 leading-relaxed uppercase tracking-tight pr-8">{wikiGroup.diagram_prompt}</p>
                        </div>

                        {/* Exercises List container */}
                        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-6 pb-6 min-h-0 font-sans">
                          {/* 3D Interactive Exercises First */}
                          {interactiveExercises.length > 0 && (
                            <div className="space-y-3">
                              <span className="text-[9.5px] font-black text-cyan-400 uppercase tracking-widest block">Simulaciones Científicas en 3D</span>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {interactiveExercises.map((e) => (
                                  <div key={e.id} className="bg-gradient-to-br from-cyan-950/20 to-black/60 border border-cyan-500/20 p-4 rounded-xl hover:border-cyan-500/60 transition-all flex flex-col justify-between">
                                    <div>
                                      <div className="flex justify-between items-start mb-2">
                                        <h5 className="font-black text-sm text-white uppercase italic tracking-tight">{e.name}</h5>
                                        <span className="text-[8px] font-black text-cyan-400 tracking-widest uppercase bg-cyan-400/10 border border-cyan-400/20 px-1.5 py-0.5 rounded-md">{e.difficulty}</span>
                                      </div>
                                      <p className="text-[10px] text-white/40 uppercase tracking-tight mt-1">Equipo: {e.equipment}</p>
                                    </div>
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        setSelectedMuscle(e.muscle);
                                        setSelectedExercise(e);
                                        setActiveTab('TRAINING');
                                        if (e.muscle === 'back' || e.muscle === 'glutes' || e.muscle === 'hamstrings' || e.muscle === 'calves' || e.muscle === 'lower_back' || e.muscle === 'triceps') {
                                          setBodyView('back');
                                        } else {
                                          setBodyView('front');
                                        }
                                      }}
                                      className="mt-4 bg-cyan-500 hover:bg-cyan-400 text-black font-black uppercase text-[8px] tracking-widest py-3.5 rounded-lg transition-all cursor-pointer"
                                    >
                                      Entrenar en Escenario 3D
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Extra Base Exercises from Wiki Checklist */}
                          <div className="space-y-3 pt-2">
                            <span className="text-[9.5px] font-black text-red-400 uppercase tracking-widest block">Guía Básica de Ejercitación en Gimnasio</span>
                            <div className="space-y-3">
                              {staticWikiExercises.map((ex, i) => (
                                <div key={ex.id || i} className="bg-white/5 border border-white/5 hover:border-red-500/20 p-4.5 rounded-xl transition-all">
                                  <div className="flex justify-between items-center mb-2.5">
                                    <h5 className="font-semibold text-xs text-white uppercase tracking-tight">{i + 1}. {ex.name}</h5>
                                    <div className="flex gap-2">
                                      <span className="text-[8px] font-medium text-white/30 uppercase tracking-widest py-0.5 px-2 bg-white/[0.03] rounded-md border border-white/5">{ex.equipment}</span>
                                      <span className="text-[7.5px] font-black text-red-400 tracking-widest uppercase bg-red-500/5 border border-red-500/15 px-1.5 py-0.5 rounded-sm">{ex.difficulty}</span>
                                    </div>
                                  </div>
                                  {/* Auto-instructions generator */}
                                  <div className="space-y-1 text-[10px] text-white/50 uppercase tracking-tight">
                                    <p className="flex items-start gap-2"><span className="text-red-500 font-bold">•</span> Extiende completamente el músculo {wikiGroup.name.toLowerCase()} de forma controlada en la fase negativa.</p>
                                    <p className="flex items-start gap-2"><span className="text-red-500 font-bold">•</span> Aplica tensión continua máxima en la contracción peak por 1-2 segundos.</p>
                                    <p className="flex items-start gap-2"><span className="text-red-500 font-bold">•</span> Evita el balanceo inercial y mantén estabilizados los hombros y el core de base.</p>
                                  </div>
                                  <div className="mt-3.5 flex justify-end">
                                    <button
                                      onClick={() => {
                                        addPoints(15);
                                        setPurchaseSuccess({
                                          productName: `Ficha Técnica: ${ex.name}`,
                                          price: 0,
                                          pts: 15
                                        });
                                      }}
                                      className="text-[8px] font-black text-red-400 hover:text-white uppercase tracking-widest py-1 px-3 border border-red-500/10 hover:border-red-500/30 rounded-md bg-red-500/5 transition-all cursor-pointer"
                                    >
                                      Marcar como Estudiado (+15 PTS)
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </motion.div>
            ) : activeTab === 'MASTER' ? (
              <motion.div
                key="master-view"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.1 }}
                className="h-full flex flex-col pointer-events-auto min-h-0 bg-red-950/20 backdrop-blur-3xl border border-red-500/20 rounded-[2rem] p-6 md:p-10 overflow-hidden font-sans"
              >
                <div className="w-full flex flex-col gap-6 h-full overflow-hidden">
                   {/* MASTER Header with metrics */}
                   <div className="flex flex-col md:flex-row items-start md:items-center justify-between border-b border-red-500/20 pb-6 gap-4 shrink-0">
                      <div>
                         <span className="text-[9px] font-black text-red-500 uppercase tracking-[0.4em] mb-1 block">NIVEL DE PRIVILEGIO: ROOT SECURITY</span>
                         <h2 className="text-3xl md:text-5xl font-black text-white italic uppercase tracking-tighter leading-none">CONSOLA <span className="text-red-500">MASTER</span></h2>
                         <p className="text-red-400/40 text-[9.5px] font-black uppercase tracking-[0.3em] mt-1">Súper Administración de Vitrinas, Tiendas y Productos</p>
                      </div>
                      <div className="flex gap-4">
                         <div className="px-5 py-3 bg-red-500/10 border border-red-500/20 rounded-xl text-center">
                            <p className="text-red-500 font-black text-lg leading-none">{trainersList.length}</p>
                            <p className="text-red-500/40 text-[7px] uppercase font-black tracking-widest mt-1">Vitrinas / Tiendas</p>
                         </div>
                         <div className="px-5 py-3 bg-red-500/10 border border-red-500/20 rounded-xl text-center">
                            <p className="text-red-500 font-black text-lg leading-none">{productsList.length}</p>
                            <p className="text-red-500/40 text-[7px] uppercase font-black tracking-widest mt-1">Productos</p>
                         </div>
                         <div className="px-5 py-3 bg-red-500/10 border border-red-500/20 rounded-xl text-center">
                            <p className="text-red-500 font-black text-lg leading-none">{routinesList.length}</p>
                            <p className="text-red-500/40 text-[7px] uppercase font-black tracking-widest mt-1">Rutinas</p>
                         </div>
                      </div>
                   </div>

                   {/* Sub-navigation to switch between target entities */}
                   <div className="flex bg-black/60 border border-red-500/15 p-1.5 rounded-2xl shrink-0 gap-1.5 md:w-fit">
                      <button
                        onClick={() => setAdminActiveSubTab('coaches')}
                        className={`flex-1 md:flex-initial px-5 py-2.5 rounded-xl text-[9.5px] font-black uppercase tracking-widest transition-all cursor-pointer ${adminActiveSubTab === 'coaches' ? 'bg-red-600 text-white shadow-md' : 'text-red-500/60 hover:text-red-500 hover:bg-red-500/5'}`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <Users className="w-3.5 h-3.5" />
                          Tiendas/Coaches
                        </div>
                      </button>
                      <button
                        onClick={() => setAdminActiveSubTab('products')}
                        className={`flex-1 md:flex-initial px-5 py-2.5 rounded-xl text-[9.5px] font-black uppercase tracking-widest transition-all cursor-pointer ${adminActiveSubTab === 'products' ? 'bg-red-600 text-white shadow-md' : 'text-red-500/60 hover:text-red-500 hover:bg-red-500/5'}`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <ShoppingBag className="w-3.5 h-3.5" />
                          Productos
                        </div>
                      </button>
                      <button
                        onClick={() => setAdminActiveSubTab('routines')}
                        className={`flex-1 md:flex-initial px-5 py-2.5 rounded-xl text-[9.5px] font-black uppercase tracking-widest transition-all cursor-pointer ${adminActiveSubTab === 'routines' ? 'bg-red-600 text-white shadow-md' : 'text-red-500/60 hover:text-red-500 hover:bg-red-500/5'}`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <Dumbbell className="w-3.5 h-3.5" />
                          Rutinas
                        </div>
                      </button>
                   </div>

                   {/* Main Management Canvas split layout */}
                   <div className="flex-1 overflow-hidden min-h-0 flex flex-col lg:flex-row gap-6">
                      {/* Left: Interactive Entity Lists */}
                      <div className="flex-1 bg-black/50 border border-red-500/10 rounded-3xl p-5 md:p-6 flex flex-col overflow-hidden min-h-0">
                         <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/5 shrink-0">
                            <h4 className="text-xs font-black text-white uppercase tracking-wider italic">
                              {adminActiveSubTab === 'coaches' ? 'Registros de Vitrinas / Tiendas Activas' : adminActiveSubTab === 'products' ? 'Productos del Catálogo General' : 'Rutinas de Entrenamiento'}
                            </h4>
                            <span className="text-[9px] font-black text-red-500 uppercase tracking-widest bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded-md">
                              Total: {adminActiveSubTab === 'coaches' ? trainersList.length : adminActiveSubTab === 'products' ? productsList.length : routinesList.length}
                            </span>
                         </div>

                         {/* Table Scroll Zone */}
                         <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar-red space-y-3 pb-6 min-h-0">
                            {adminActiveSubTab === 'coaches' && (
                              trainersList.map((t) => (
                                 <div key={t.id} className="p-3.5 bg-white/[0.02] border border-white/5 hover:border-red-500/20 rounded-xl transition-all flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                       <img src={t.image} className="w-12 h-12 rounded-lg object-cover border border-white/10 shrink-0" alt="" />
                                       <div>
                                          <div className="flex items-center gap-2">
                                             <h5 className="font-black text-sm text-white italic uppercase tracking-tight">{t.name}</h5>
                                             <span className="text-[7.5px] font-black text-cyan-400 bg-cyan-400/10 border border-cyan-400/20 px-1 py-0.1 rounded uppercase">{t.specialty}</span>
                                          </div>
                                          <p className="text-[9.5px] text-white/40 uppercase font-black tracking-widest mt-0.5">Exp: {t.experience} • ID: {t.id}</p>
                                       </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 w-full md:w-auto self-end md:self-center">
                                       <button
                                         onClick={() => {
                                           setShowcaseTrainerId(t.id);
                                           setActiveTab('COACHES');
                                         }}
                                         title="Ver Vitrina"
                                         className="flex-1 md:flex-initial flex items-center justify-center gap-1 px-3 py-2 bg-cyan-500/10 hover:bg-cyan-500/25 border border-cyan-500/30 text-cyan-400 text-[8.5px] font-black uppercase tracking-widest rounded-lg transition-all cursor-pointer"
                                       >
                                          <Eye className="w-3.5 h-3.5" />
                                          VER TIENDA
                                       </button>
                                       <button
                                         onClick={() => {
                                           setEditingTrainer(t);
                                           setTrainerForm({
                                             id: t.id,
                                             name: t.name,
                                             specialty: t.specialty,
                                             experience: t.experience,
                                             bio: t.bio,
                                             image: t.image
                                           });
                                         }}
                                         className="flex items-center justify-center p-2.5 border border-red-500/20 hover:border-red-500/60 bg-red-500/5 hover:bg-red-500/10 text-red-500 rounded-lg transition-all cursor-pointer"
                                       >
                                          <Edit3 className="w-3.5 h-3.5" />
                                       </button>
                                       <button
                                         onClick={() => {
                                            setTrainersList(prev => prev.filter(x => x.id !== t.id));
                                            if (showcaseTrainerId === t.id) setShowcaseTrainerId(null);
                                         }}
                                         className="flex items-center justify-center p-2.5 border border-red-500/25 hover:bg-red-600/30 text-red-400 hover:text-white rounded-lg transition-all cursor-pointer"
                                       >
                                          <Trash2 className="w-3.5 h-3.5" />
                                       </button>
                                    </div>
                                 </div>
                              ))
                            )}

                            {adminActiveSubTab === 'products' && (
                              productsList.map((p) => {
                                 const owner = trainersList.find(c => c.id === p.trainerId);
                                 return (
                                    <div key={p.id} className="p-3.5 bg-white/[0.02] border border-white/5 hover:border-red-500/20 rounded-xl transition-all flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                       <div className="flex items-center gap-3">
                                          <img src={p.image} className="w-12 h-12 rounded-lg object-cover border border-white/10 shrink-0" alt="" />
                                          <div>
                                             <div className="flex items-center gap-2 flex-wrap">
                                                <h5 className="font-bold text-sm text-white uppercase tracking-tight">{p.name}</h5>
                                                <span className="text-[7.5px] font-black text-cyan-400 bg-cyan-400/10 border border-cyan-400/20 px-1 py-0.1 rounded">{p.category}</span>
                                                <span className="text-[8.5px] font-black text-red-500 bg-red-500/10 border border-red-500/20 px-1.5 py-0.1 rounded">${p.price}</span>
                                             </div>
                                             <p className="text-[9.5px] text-white/40 uppercase font-bold tracking-tight mt-0.5">
                                               Vitrina: {owner ? owner.name : 'Tienda General'}
                                             </p>
                                          </div>
                                       </div>
                                       <div className="flex gap-2 w-full md:w-auto self-end md:self-center justify-end">
                                          <button
                                            onClick={() => {
                                              setEditingProduct(p);
                                              setProductForm({
                                                id: p.id,
                                                name: p.name,
                                                category: p.category,
                                                price: p.price,
                                                description: p.description,
                                                image: p.image,
                                                trainerId: p.trainerId || ''
                                              });
                                            }}
                                            className="p-2.5 border border-red-500/20 hover:border-red-500/60 bg-red-500/5 hover:bg-red-500/10 text-red-500 rounded-lg transition-all cursor-pointer"
                                          >
                                             <Edit3 className="w-3.5 h-3.5" />
                                          </button>
                                          <button
                                            onClick={() => setProductsList(prev => prev.filter(x => x.id !== p.id))}
                                            className="p-2.5 border border-red-500/25 hover:bg-red-600/30 text-red-400 hover:text-white rounded-lg transition-all cursor-pointer"
                                          >
                                             <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                       </div>
                                    </div>
                                 );
                              })
                            )}

                            {adminActiveSubTab === 'routines' && (
                              routinesList.map((r) => {
                                 const owner = trainersList.find(c => c.id === r.trainerId);
                                 return (
                                    <div key={r.id} className="p-3.5 bg-white/[0.02] border border-white/5 hover:border-red-500/20 rounded-xl transition-all flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                       <div className="flex items-center gap-3">
                                          <img src={r.image} className="w-12 h-12 rounded-lg object-cover border border-white/10 shrink-0" alt="" />
                                          <div>
                                             <div className="flex items-center gap-2">
                                                <h5 className="font-bold text-sm text-white uppercase tracking-tight">{r.name}</h5>
                                                <span className="text-[7.5px] font-black text-cyan-400 bg-cyan-400/10 border border-cyan-400/20 px-1 py-0.1 rounded">{r.difficulty}</span>
                                                <span className="text-[8px] font-black text-white/50 bg-white/10 px-1.5 py-0.1 rounded">{r.duration}</span>
                                             </div>
                                             <p className="text-[9.5px] text-white/40 uppercase font-bold tracking-tight mt-0.5">
                                               Proporcionada por: {owner ? owner.name : 'Gym General'}
                                             </p>
                                          </div>
                                       </div>
                                       <div className="flex gap-2 w-full md:w-auto self-end md:self-center justify-end">
                                          <button
                                            onClick={() => {
                                              setEditingRoutine(r);
                                              setRoutineForm({
                                                id: r.id,
                                                name: r.name,
                                                description: r.description,
                                                difficulty: r.difficulty,
                                                duration: r.duration,
                                                image: r.image,
                                                trainerId: r.trainerId || ''
                                              });
                                            }}
                                            className="p-2.5 border border-red-500/20 hover:border-red-500/60 bg-red-500/5 hover:bg-red-500/10 text-red-500 rounded-lg transition-all cursor-pointer"
                                          >
                                             <Edit3 className="w-3.5 h-3.5" />
                                          </button>
                                          <button
                                            onClick={() => setRoutinesList(prev => prev.filter(x => x.id !== r.id))}
                                            className="p-2.5 border border-red-500/25 hover:bg-red-600/30 text-red-400 hover:text-white rounded-lg transition-all cursor-pointer"
                                          >
                                             <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                       </div>
                                    </div>
                                 );
                              })
                            )}
                         </div>
                      </div>

                      {/* Right: Dynamic Create / Edit Action Form */}
                      <div className="w-full lg:w-96 bg-black/60 border border-red-500/10 rounded-3xl p-6 flex flex-col justify-between overflow-y-auto custom-scrollbar-red">
                         <div>
                            <div className="flex items-center gap-2 border-b border-white/5 pb-3 mb-5">
                               <Plus className="w-4 h-4 text-red-500 animate-pulse" />
                               <h4 className="text-sm font-black text-white uppercase tracking-wider italic">
                                 {adminActiveSubTab === 'coaches'
                                    ? (editingTrainer ? 'Editar Coach / Tienda' : 'Agregar Coach / Tienda')
                                    : adminActiveSubTab === 'products'
                                    ? (editingProduct ? 'Editar Producto' : 'Agregar Producto')
                                    : (editingRoutine ? 'Editar Rutina' : 'Agregar Rutina')
                                 }
                               </h4>
                            </div>

                            {/* Form fields based on Active Subtab */}
                            {adminActiveSubTab === 'coaches' && (
                               <div className="space-y-4">
                                  <div className="space-y-1">
                                     <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">Nombre Completo</label>
                                     <input
                                       type="text"
                                       value={trainerForm.name}
                                       placeholder="Ej: Marcos Diaz"
                                       onChange={(e) => setTrainerForm(prev => ({...prev, name: e.target.value}))}
                                       className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50"
                                     />
                                  </div>
                                  <div className="space-y-1">
                                     <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">Especialidad</label>
                                     <input
                                       type="text"
                                       value={trainerForm.specialty}
                                       placeholder="Ej: Fuerza, Hipertrofia o Cardio"
                                       onChange={(e) => setTrainerForm(prev => ({...prev, specialty: e.target.value}))}
                                       className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50"
                                     />
                                  </div>
                                  <div className="space-y-1">
                                     <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">Experiencia Laboral</label>
                                     <input
                                       type="text"
                                       value={trainerForm.experience}
                                       placeholder="Ej: 8 Años Certificado"
                                       onChange={(e) => setTrainerForm(prev => ({...prev, experience: e.target.value}))}
                                       className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50"
                                     />
                                  </div>
                                  <div className="space-y-1">
                                     <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">Biografía Corta</label>
                                     <textarea
                                       rows={3}
                                       value={trainerForm.bio}
                                       placeholder="Describe el enfoque del coach o tienda..."
                                       onChange={(e) => setTrainerForm(prev => ({...prev, bio: e.target.value}))}
                                       className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50 resize-none"
                                     />
                                  </div>
                                  <div className="space-y-1">
                                     <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">URL de Imagen</label>
                                     <input
                                       type="text"
                                       value={trainerForm.image}
                                       placeholder="URL de Unsplash u otra foto"
                                       onChange={(e) => setTrainerForm(prev => ({...prev, image: e.target.value}))}
                                       className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50"
                                     />
                                  </div>
                               </div>
                            )}

                            {adminActiveSubTab === 'products' && (
                               <div className="space-y-4">
                                  <div className="space-y-1">
                                     <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">Nombre del Producto</label>
                                     <input
                                       type="text"
                                       value={productForm.name}
                                       placeholder="Ej: Guantes de Cuero Pro"
                                       onChange={(e) => setProductForm(prev => ({...prev, name: e.target.value}))}
                                       className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50"
                                     />
                                  </div>
                                  <div className="grid grid-cols-2 gap-3">
                                     <div className="space-y-1">
                                        <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">Categoría</label>
                                        <select
                                          value={productForm.category}
                                          onChange={(e) => setProductForm(prev => ({...prev, category: e.target.value as any}))}
                                          className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50 appearance-none cursor-pointer"
                                        >
                                           <option value="Ropa" className="bg-black">Ropa</option>
                                           <option value="Nutrición" className="bg-black">Nutrición</option>
                                           <option value="Comunidad" className="bg-black">Comunidad</option>
                                        </select>
                                     </div>
                                     <div className="space-y-1">
                                        <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">Precio (USD)</label>
                                        <input
                                          type="number"
                                          value={productForm.price || ''}
                                          placeholder="Ej: 35"
                                          onChange={(e) => setProductForm(prev => ({...prev, price: parseInt(e.target.value) || 0}))}
                                          className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50"
                                        />
                                     </div>
                                  </div>
                                  <div className="space-y-1">
                                     <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">Descripción</label>
                                     <textarea
                                       rows={2}
                                       value={productForm.description}
                                       placeholder="Beneficios o composición..."
                                       onChange={(e) => setProductForm(prev => ({...prev, description: e.target.value}))}
                                       className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50 resize-none"
                                     />
                                  </div>
                                  <div className="space-y-1">
                                     <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">URL de Imagen</label>
                                     <input
                                       type="text"
                                       value={productForm.image}
                                       placeholder="Foto descriptiva"
                                       onChange={(e) => setProductForm(prev => ({...prev, image: e.target.value}))}
                                       className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50"
                                     />
                                  </div>
                                  <div className="space-y-1">
                                     <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">Vincular a Tienda / Coach</label>
                                     <select
                                       value={productForm.trainerId}
                                       onChange={(e) => setProductForm(prev => ({...prev, trainerId: e.target.value}))}
                                       className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50 appearance-none cursor-pointer"
                                     >
                                        <option value="" className="bg-black">Tienda General (Ninguno)</option>
                                        {trainersList.map(t => (
                                           <option key={t.id} value={t.id} className="bg-black">{t.name}</option>
                                        ))}
                                     </select>
                                  </div>
                               </div>
                            )}

                            {adminActiveSubTab === 'routines' && (
                               <div className="space-y-4">
                                  <div className="space-y-1">
                                     <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">Nombre de la Rutina</label>
                                     <input
                                       type="text"
                                       value={routineForm.name}
                                       placeholder="Ej: Calistenia Básica"
                                       onChange={(e) => setRoutineForm(prev => ({...prev, name: e.target.value}))}
                                       className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50"
                                     />
                                  </div>
                                  <div className="grid grid-cols-2 gap-3">
                                     <div className="space-y-1">
                                        <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">Dificultad</label>
                                        <select
                                          value={routineForm.difficulty}
                                          onChange={(e) => setRoutineForm(prev => ({...prev, difficulty: e.target.value as any}))}
                                          className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50 appearance-none cursor-pointer"
                                        >
                                           <option value="Principiante" className="bg-black">Principiante</option>
                                           <option value="Intermedio" className="bg-black">Intermedio</option>
                                           <option value="Avanzado" className="bg-black">Avanzado</option>
                                        </select>
                                     </div>
                                     <div className="space-y-1">
                                        <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">Duración</label>
                                        <input
                                          type="text"
                                          value={routineForm.duration}
                                          placeholder="Ej: 45 min"
                                          onChange={(e) => setRoutineForm(prev => ({...prev, duration: e.target.value}))}
                                          className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50"
                                        />
                                     </div>
                                  </div>
                                  <div className="space-y-1">
                                     <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">Descripción Técnica</label>
                                     <textarea
                                       rows={2}
                                       value={routineForm.description}
                                       placeholder="Ej: Foco excéntrico y desarrollo de fuerza del tren superior..."
                                       onChange={(e) => setRoutineForm(prev => ({...prev, description: e.target.value}))}
                                       className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50 resize-none"
                                     />
                                  </div>
                                  <div className="space-y-1">
                                     <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">URL de Imagen</label>
                                     <input
                                       type="text"
                                       value={routineForm.image}
                                       placeholder="URL de fondo ilustrativo"
                                       onChange={(e) => setRoutineForm(prev => ({...prev, image: e.target.value}))}
                                       className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50"
                                     />
                                  </div>
                                  <div className="space-y-1">
                                     <label className="text-[8px] font-black text-white/40 uppercase tracking-widest">Asignar a Showcase Store</label>
                                     <select
                                       value={routineForm.trainerId}
                                       onChange={(e) => setRoutineForm(prev => ({...prev, trainerId: e.target.value}))}
                                       className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-red-500/50 appearance-none cursor-pointer"
                                     >
                                        <option value="" className="bg-black">Catálogo General (Ninguno)</option>
                                        {trainersList.map(t => (
                                           <option key={t.id} value={t.id} className="bg-black">{t.name}</option>
                                        ))}
                                     </select>
                                  </div>
                               </div>
                            )}
                         </div>

                         {/* Form Actions buttons */}
                         <div className="mt-8 pt-4 border-t border-white/5 space-y-2 shrink-0">
                            <Button
                              onClick={() => {
                                 if (adminActiveSubTab === 'coaches') {
                                    // Submit coach
                                    if (!trainerForm.name || !trainerForm.specialty) return;
                                    const img = trainerForm.image || 'https://images.unsplash.com/photo-1571019623129-fbf8a469f06d?q=80&w=800';
                                    if (editingTrainer) {
                                       setTrainersList(prev => prev.map(t => t.id === editingTrainer.id ? { ...t, name: trainerForm.name, specialty: trainerForm.specialty, experience: trainerForm.experience, bio: trainerForm.bio, image: img } : t));
                                    } else {
                                       const newId = `coach-${Date.now()}`;
                                       setTrainersList(prev => [...prev, { id: newId, name: trainerForm.name, specialty: trainerForm.specialty, experience: trainerForm.experience || '3 Años', bio: trainerForm.bio || 'Coach Profesional', image: img }]);
                                    }
                                    setEditingTrainer(null);
                                    setTrainerForm({ id: '', name: '', specialty: '', experience: '', bio: '', image: '' });
                                 } else if (adminActiveSubTab === 'products') {
                                    // Submit product
                                    if (!productForm.name) return;
                                    const img = productForm.image || 'https://images.unsplash.com/photo-1518310383802-640c2de311b2?q=80&w=800';
                                    if (editingProduct) {
                                       setProductsList(prev => prev.map(p => p.id === editingProduct.id ? { ...p, name: productForm.name, category: productForm.category, price: productForm.price, description: productForm.description, image: img, trainerId: productForm.trainerId || undefined } : p));
                                    } else {
                                       const newId = `prod-${Date.now()}`;
                                       setProductsList(prev => [...prev, { id: newId, name: productForm.name, category: productForm.category, price: productForm.price || 0, description: productForm.description || 'Producto PREMIUM', image: img, trainerId: productForm.trainerId || undefined }]);
                                    }
                                    setEditingProduct(null);
                                    setProductForm({ id: '', name: '', category: 'Ropa', price: 0, description: '', image: '', trainerId: '' });
                                 } else if (adminActiveSubTab === 'routines') {
                                    // Submit routine
                                    if (!routineForm.name) return;
                                    const img = routineForm.image || 'https://images.unsplash.com/photo-1541534741688-6078c6bfb5c5?q=80&w=800';
                                    if (editingRoutine) {
                                       setRoutinesList(prev => prev.map(r => r.id === editingRoutine.id ? { ...r, name: routineForm.name, description: routineForm.description, difficulty: routineForm.difficulty, duration: routineForm.duration, image: img, trainerId: routineForm.trainerId || undefined } : r));
                                    } else {
                                       const newId = `routine-${Date.now()}`;
                                       setRoutinesList(prev => [...prev, { id: newId, name: routineForm.name, description: routineForm.description || 'Rutina de alto impacto', difficulty: routineForm.difficulty, duration: routineForm.duration || '30 min', exerciseIds: ['push-ups', 'bench-press'], image: img, trainerId: routineForm.trainerId || undefined }]);
                                    }
                                    setEditingRoutine(null);
                                    setRoutineForm({ id: '', name: '', description: '', difficulty: 'Principiante', duration: '30 min', image: '', trainerId: '' });
                                 }
                              }}
                              className="w-full bg-red-600 hover:bg-red-500 text-white font-black uppercase text-[10px] tracking-widest py-5 rounded-xl transition-all shadow-[0_5px_15px_rgba(220,38,38,0.2)] cursor-pointer"
                            >
                               {editingTrainer || editingProduct || editingRoutine ? 'GUARDAR MODIFICACIONES' : 'REGISTRAR NUEVO ITEM'}
                            </Button>

                            {(editingTrainer || editingProduct || editingRoutine) && (
                               <Button
                                 onClick={() => {
                                    setEditingTrainer(null);
                                    setEditingProduct(null);
                                    setEditingRoutine(null);
                                    setTrainerForm({ id: '', name: '', specialty: '', experience: '', bio: '', image: '' });
                                    setProductForm({ id: '', name: '', category: 'Ropa', price: 0, description: '', image: '', trainerId: '' });
                                    setRoutineForm({ id: '', name: '', description: '', difficulty: 'Principiante', duration: '30 min', image: '', trainerId: '' });
                                 }}
                                 variant="outline"
                                 className="w-full border-white/10 hover:border-white/30 text-white/60 hover:text-white font-black uppercase text-[9px] tracking-widest py-3 rounded-lg transition-all cursor-pointer"
                               >
                                  CANCELAR EDICIÓN
                               </Button>
                            )}
                         </div>
                      </div>
                   </div>
                </div>
              </motion.div>
            ) : (
              <motion.div 
                key="store-view"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="h-full flex flex-col pointer-events-auto overflow-hidden"
              >
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8">
                   <div>
                      <h2 className="text-3xl font-black text-white italic uppercase tracking-tighter">Tienda <span className="text-cyan-400">Bienvenido</span></h2>
                      <p className="text-white/40 text-[10px] font-black uppercase tracking-[0.4em] mt-1">Equipamiento, Nutrición y Comunidad</p>
                   </div>
                   <div className="flex flex-wrap gap-2">
                      <button 
                        onClick={() => setStoreCategory('Todos')}
                        className={`px-4 py-2 border border-white/10 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-colors ${storeCategory === 'Todos' ? 'bg-cyan-500 text-black border-cyan-500' : 'bg-white/5 text-white/40 hover:text-white'}`}
                      >
                        Todos
                      </button>
                      <button 
                        onClick={() => setStoreCategory('Ropa')}
                        className={`px-4 py-2 border border-white/10 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-colors ${storeCategory === 'Ropa' ? 'bg-cyan-500 text-black border-cyan-500' : 'bg-white/5 text-white/40 hover:text-white'}`}
                      >
                        Ropa
                      </button>
                      <button 
                        onClick={() => setStoreCategory('Nutrición')}
                        className={`px-4 py-2 border border-white/10 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-colors ${storeCategory === 'Nutrición' ? 'bg-cyan-500 text-black border-cyan-500' : 'bg-white/5 text-white/40 hover:text-white'}`}
                      >
                        Nutrición
                      </button>
                      <button 
                        onClick={() => setStoreCategory('Comunidad')}
                        className={`px-4 py-2 border border-white/10 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-colors ${storeCategory === 'Comunidad' ? 'bg-cyan-500 text-black border-cyan-500' : 'bg-white/5 text-white/40 hover:text-white'}`}
                      >
                        Comunidad
                      </button>
                   </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 overflow-y-auto pr-2 custom-scrollbar pb-12">
                   {filteredProducts.map((p) => (
                     <div key={p.id} className="group bg-black/40 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden hover:border-cyan-500/50 transition-all">
                        <div className="aspect-video relative overflow-hidden">
                           <img src={p.image} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt={p.name} />
                           <div className="absolute top-4 left-4">
                              <span className="px-3 py-1 bg-black/60 backdrop-blur-xl rounded-full text-[8px] font-bold text-white uppercase tracking-widest border border-white/10">
                                {p.category}
                              </span>
                           </div>
                        </div>
                        <div className="p-6">
                           <h4 className="text-lg font-black text-white uppercase italic tracking-tight group-hover:text-cyan-400 transition-colors">{p.name}</h4>
                           <p className="text-white/40 text-[10px] mt-2 mb-6 line-clamp-2 leading-relaxed uppercase tracking-tight">{p.description}</p>
                           <div className="flex items-center justify-between">
                              <span className="text-xl font-black text-cyan-400">${p.price}</span>
                              <Button size="sm" className="bg-white text-black font-black uppercase text-[10px] tracking-widest h-8 px-6 hover:bg-cyan-500 hover:text-white rounded-none">
                                 Comprar
                              </Button>
                           </div>
                        </div>
                     </div>
                   ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Loyalty Card Panel (Overlay) */}
        <AnimatePresence>
          {hasLoyaltyCard && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, x: 20 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.9, x: 20 }}
              className="fixed top-24 md:top-32 right-4 md:right-12 w-[calc(100%-2rem)] sm:w-96 bg-gradient-to-br from-cyan-600 to-cyan-900 rounded-[2rem] md:rounded-[2.5rem] p-6 md:p-10 shadow-[0_0_100px_rgba(6,182,212,0.3)] z-50 pointer-events-auto border border-white/20"
            >
              <div className="flex justify-between items-start mb-12">
                 <div className="w-12 h-12 bg-white/20 backdrop-blur-xl rounded-2xl flex items-center justify-center">
                    <Star className="w-6 h-6 text-white" />
                 </div>
                 <X 
                   onClick={toggleLoyaltyCard}
                   className="w-6 h-6 text-white/40 hover:text-white cursor-pointer transition-colors" 
                 />
              </div>

              <div className="flex flex-col gap-2 mb-12">
                 <p className="text-[10px] font-black text-white/60 uppercase tracking-[0.4em]">Fidelización Bienvenido</p>
                 <h3 className="text-4xl font-black text-white italic uppercase tracking-tighter">Elite Member</h3>
                 <p className="text-cyan-200/60 text-[10px] font-bold uppercase tracking-widest mt-1">ID: #0829-X92-2024</p>
              </div>

              <div className="space-y-6">
                 <div className="space-y-2">
                    <div className="flex justify-between text-[10px] font-black text-white uppercase tracking-widest">
                       <span>Progreso Nivel 3</span>
                       <span>{Math.round((loyaltyPoints / 500) * 100)}%</span>
                    </div>
                    <div className="h-1.5 w-full bg-black/20 rounded-full overflow-hidden">
                       <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${(loyaltyPoints / 500) * 100}%` }}
                        className="h-full bg-white shadow-[0_0_15px_white]" 
                       />
                    </div>
                 </div>

                 <div className="grid grid-cols-2 gap-4">
                    <div className="bg-black/20 p-4 rounded-2xl backdrop-blur-xl border border-white/5">
                       <Gift className="w-4 h-4 text-cyan-400 mb-2" />
                       <p className="text-white text-xs font-black uppercase">2 Recompensas</p>
                       <p className="text-white/40 text-[8px] uppercase font-bold mt-1">Disponibles</p>
                    </div>
                    <div className="bg-black/20 p-4 rounded-2xl backdrop-blur-xl border border-white/5">
                       <CreditCard className="w-4 h-4 text-cyan-400 mb-2" />
                       <p className="text-white text-xs font-black uppercase">Cache Back</p>
                       <p className="text-white/40 text-[8px] uppercase font-bold mt-1">15% Activo</p>
                    </div>
                 </div>
              </div>

              <div className="mt-12 flex justify-center">
                 <div className="w-full py-4 border-t border-white/10 text-center">
                    <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.5em]">Bienvenido Global Ecosystem</p>
                 </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile Bottom Navigation */}
      <motion.div 
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className="lg:hidden fixed bottom-6 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-lg bg-black/80 backdrop-blur-3xl border border-white/10 rounded-3xl p-3 flex items-center justify-around pointer-events-auto z-50 shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
      >
        <button 
          onClick={() => { setActiveTab('TRAINING'); setSelectedExercise(null); }}
          className={`flex flex-col items-center gap-1.5 px-4 py-2 rounded-2xl transition-all ${activeTab === 'TRAINING' ? 'text-cyan-400 bg-cyan-500/10 border border-cyan-500/20' : 'text-white/40'}`}
        >
          <Target className="w-5 h-5" />
          <span className="text-[8px] font-black uppercase tracking-wider">Entrenar</span>
        </button>
        <button 
          onClick={() => { setActiveTab('HOME'); setSelectedRoutine(null); setSelectedExercise(null); }}
          className={`flex flex-col items-center gap-1.5 px-4 py-2 rounded-2xl transition-all ${activeTab === 'HOME' ? 'text-cyan-400 bg-cyan-500/10 border border-cyan-500/20' : 'text-white/40'}`}
        >
          <Home className="w-5 h-5" />
          <span className="text-[8px] font-black uppercase tracking-wider">Casa</span>
        </button>
        <button 
          onClick={() => { setActiveTab('COACHES'); setSelectedTrainer(null); setSelectedExercise(null); }}
          className={`flex flex-col items-center gap-1.5 px-4 py-2 rounded-2xl transition-all ${activeTab === 'COACHES' ? 'text-cyan-400 bg-cyan-500/10 border border-cyan-500/20' : 'text-white/40'}`}
        >
          <User className="w-5 h-5" />
          <span className="text-[8px] font-black uppercase tracking-wider">Coach</span>
        </button>
        <button 
          onClick={() => { setActiveTab('ROUTINES'); setSelectedRoutine(null); setSelectedExercise(null); }}
          className={`flex flex-col items-center gap-1.5 px-4 py-2 rounded-2xl transition-all ${activeTab === 'ROUTINES' ? 'text-cyan-400 bg-cyan-500/10 border border-cyan-500/20' : 'text-white/40'}`}
        >
          <Layout className="w-5 h-5" />
          <span className="text-[8px] font-black uppercase tracking-wider">Rutinas</span>
        </button>
        <button 
          onClick={() => { setActiveTab('STORE'); setSelectedExercise(null); }}
          className={`flex flex-col items-center gap-1.5 px-4 py-2 rounded-2xl transition-all ${activeTab === 'STORE' ? 'text-cyan-400 bg-cyan-500/10 border border-cyan-500/20' : 'text-white/40'}`}
        >
          <ShoppingBag className="w-5 h-5" />
          <span className="text-[8px] font-black uppercase tracking-wider">Tienda</span>
        </button>
      </motion.div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(6, 182, 212, 0.2);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(6, 182, 212, 0.4);
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};
